import { destPreferenceRank } from "./airportDoctrine";
import { recordDiagnostic } from "./diagnosticsLog";
import { MOCK_GROK_KEY, mockGrokEnabled, mockGrokFetch } from "./developerMocks";
import {
  destGravityPenalty,
  grokDestinationGravityLine,
  liveDeskBases,
  nearBaseDestSample,
  orderByBaseGravity,
  parkedFieldNearBases,
  softPreferIcaos,
} from "./baseGravity";
import {
  destRegionOf,
  formatMintAvoidancePrompt,
  hardAvoidPairLabels,
  hopNm,
  LARGE_ALLOWLIST_ICAO_FLOOR,
  mintAvoidanceFromDesk,
  mintDestPenalty,
  requestMatchesMintAvoidance,
  shouldExcludeMintPair,
  type InboxRouteMemoryEntry,
  type MintAvoidance,
  type RecentRouteSettings,
} from "./inboxMintMemory";
import { formatCrewPrefMintPrompt, type CrewRoutePrefs } from "./crewPrefs";
import { formatCharterOpsBriefPromptBlock } from "./charterOpsBrief";
import { hydrateSceneryAirport, resolveCoords } from "./airports";
import { timeZoneForIcao } from "./airportTimeZone";
import { type MoneyCatalogs } from "./catalogs";
import {
  alignCharterOnCopy,
  parseInboxDeparture,
  pickStdRaw,
  formatInboxStdLine,
} from "./charterDate";
import {
  GROK_INBOX_SYSTEM,
  grokRealismRulesFor,
  canCarryMxPayload,
  clampCharterPax,
  destIsMroForType,
  destsForPurpose,
  findMxPayloadOpportunity,
  isAtlanticGolfRegion,
  isGolfAllowed,
  isGolfSeasonMonth,
  jetRejectsCharterStage,
  PURPOSE_TO_FLIGHT_TYPE,
  pickSeasonalPurpose,
  preferDestsForPurpose,
  preferOriginsForPurpose,
  purposeFromFlightType,
  purposeReason,
  remapUnseasonalType,
  seasonHintForPrompt,
  type SeasonalPurposeId,
} from "./charterRealism";
import { type DestPackId } from "./destPacks";
import { haversineNm } from "./geo";
import { aircraftStatePromptLine, type AircraftState } from "./aircraftState";
import { airframeBaseIcao, companyOriginIcaos } from "./company";
import { inboxOriginIcao, type JobMode } from "./jobMode";
import { isEmptyFerry, isPayingMxPositioning, isRevenueCharter, quoteCharter } from "./ledger";
import { cargoCapFromPayload, planningCruiseKt, runwayPicPairWarning } from "./hawker800xp";
import { regionalParty, settleParty } from "./regionalParty";
import { people, pick } from "./scenario";
import { withLegs } from "./schedule";
import { halifaxMonth, halifaxYear, seasonCaption } from "./season";
import { computeLeg, eteMinutes, fuelPlan, validForAircraft } from "./generator";
import { flightPlanFromDraft, type FlightPlanDraft } from "./flightplan";
import { isCharterAssignable } from "./maintenance";
import {
  SUGGESTED_COMPANY_NAME,
  pickAssignedAircraft,
  ratedFleet,
} from "./profile";
import {
  applyContentXmlScheduleToScenery,
  contentXmlScheduleBlocked,
  icaosFromRoute,
  inventoryIcaosFromPrefers,
} from "./contentXmlSchedule";
import { missingSceneryWarning } from "./sceneryGate";
import { radioIdFor, type RadioCompany } from "./radio";
import {
  BACKHAUL_QUOTE_FACTOR,
  backhaulDepartureIso,
  backhaulEmailNote,
  hydrateItineraryKind,
  itineraryNoteForEmail,
  payingProposedLegs,
  planOutboundItinerary,
  shouldOfferBackhaul,
} from "./itinerary";
import {
  applyManualTechStop,
  applyTechStopPlanToRequest,
  effectiveTechStopAllowlist,
  findFollowOnNearDest,
  hopDistanceNm,
  needsTechStop,
  payingOrTechStopLegs,
  planTechStopRoute,
  quoteFeeBreakdown,
  requestNeedsTechStopPick,
  TECH_STOP_TRICKLE_RATE,
} from "./techStop";
import {
  applyInboxStdBias,
  formatStandbyStdPromptBlock,
  pickInboxDeparture,
} from "./inboxStd";
import type {
  AircraftSpec,
  CharterRequest,
  CompanyMxBase,
  DraftMission,
  DutyStandbyState,
  FlightLogEntry,
  FlightType,
  Mission,
  InquirySource,
  InquiryTone,
  MelItem,
  OpsDeskSettings,
  RateTables,
  SceneryAirport,
  Squawk,
} from "./types";

export const XAI_CHAT_URL = "https://api.x.ai/v1/chat/completions";
/** Desk text-brain default (Inbox Fetch + dispatcher brain). Settings can pin an older id. */
export const XAI_INBOX_MODEL = "grok-4.6";
/** Previous desk default — still pinnable from Settings. */
export const XAI_INBOX_MODEL_PREVIOUS = "grok-4.3";
export const XAI_INBOX_MODEL_FALLBACK = "grok-3-mini";

/** Retry / remint nudge sent back to Grok Inbox. Not a Settings field. */
export type GrokInboxNudge = "none" | "json_only" | "nonempty" | "relaxed" | "avoid_pairs";

export type GrokInboxResponseFormat = "json_schema" | "json_object";

export type DeskQuoteHint = NonNullable<CharterRequest["deskQuote"]>;

const FLIGHT_TYPES: FlightType[] = ["golf", "sun", "vip", "corporate", "medical"];

export type FetchInquiriesInput = {
  scenery: SceneryAirport[];
  fleet: AircraftSpec[];
  apiKey?: string;
  count?: number;
  now?: Date;
  forceDemo?: boolean;
  ratedIcaoTypes?: string[];
  /** Automated batches only. Manual Fetch and typed charters ignore this. */
  hardFilterRatings?: boolean;
  operatorName?: string;
  /** Soft-prefer these ICAOs (Community scan). */
  preferIcaos?: string[];
  sceneryGateEnabled?: boolean;
  /** When false and a scan exists, skip dests the crew does not have installed. */
  allowUninstalled?: boolean;
  /**
   * Company Dispatcher Setting. When true, mint only ICAOs from preferIcaos /
   * techStopInventory (the Content.xml / Community scan). Fail closed if empty.
   */
  scheduleOnlyContentXmlAirports?: boolean;
  jobMode?: JobMode;
  /** Fused / last-known aircraft position — assignment / ferry only. Not Inbox RFQ origin. */
  nearPosition?: { lat: number; lon: number } | null;
  /** Aircraft state — clamps Grok / seasonal STD timing. Never invent a phase. Not RFQ origin. */
  aircraftState?: AircraftState | null;
  /** Open board trips so generation can avoid obvious double-books. Occasional overlap is OK. */
  existingTrips?: { originIcao: string; destIcao: string; departureIso: string; registration?: string }[];
  /** Remaining duty / flight minutes (company CAR clock) when known. */
  dutyRemainMin?: number;
  destPacks?: DestPackId[];
  rates?: RateTables;
  catalogs?: MoneyCatalogs;
  /** Variable hourly DOC. Quote reserves use a set Mx or engine component. */
  opsDesk?: OpsDeskSettings | null;
  /** Override the desk default model. Empty uses XAI_INBOX_MODEL. */
  inboxModel?: string;
  /**
   * `idle` = cost-capped on-open / trickle mint (cheap model + shorter allow-list).
   * Manual Fetch stays `full` (default).
   */
  inboxPromptKind?: "full" | "idle";
  /**
   * Optional cap on Grok HTTP calls for this fetch. Idle mint passes the
   * remaining daily budget. Omit to use the per-kind budget (max 2).
   */
  inboxCallBudget?: number;
  /**
   * Internal Grok retry nudge. Desk Fetch / idle mint set this while
   * re-asking — not a Settings field.
   */
  inboxGrokNudge?: GrokInboxNudge;
  /** City-pairs the last Grok set lost to remint / Content.xml / cool-down. */
  inboxAvoidPairs?: string[];
  /** Test / desk override. Defaults to global fetch. */
  fetchImpl?: typeof fetch;
  /** Deterministic seasonal fallback seed. */
  seed?: number;
  homeIcao?: string;
  /** Extra company bases (Settings). Inbox origin priority after the tail's assigned base. */
  extraBases?: string[];
  /** Company MX bases. Empty does not invent a shop. */
  mxBases?: readonly CompanyMxBase[];
  /** Open squawks — mx-payload charters skip ground-severity tails. */
  squawks?: Squawk[];
  /** Open MEL — grounding items block paying mx ferry. */
  melItems?: MelItem[];
  /** Settings / career imported tech-stop ICAOs. */
  techStopIcaos?: string[];
  /** Last Community / Content.xml scan ICAOs. */
  techStopInventory?: string[];
  /** Open inbox used to credit empty-return when a follow-on near dest exists. */
  boardInbox?: CharterRequest[];
  /** Open board trips used the same way as boardInbox. */
  boardMissions?: ReadonlyArray<Pick<Mission, "originIcao" | "destIcao"> & Partial<Pick<Mission, "id" | "status" | "flightType" | "inboxRequestId">>>;
  /** Dispatcher-written company / charter-type doctrine for Grok Fetch. */
  charterOpsBriefTitle?: string;
  charterOpsBrief?: string;
  /** Armed Duty Standby — bias mint STDs into the watch window. */
  dutyStandby?: Pick<DutyStandbyState, "armed" | "windows"> | null;
  /** Declined / deleted city-pair fingerprints (company snapshot). */
  inboxRouteMemory?: InboxRouteMemoryEntry[];
  /** Trip Log — recently completed flights (do not invent). */
  flightLogs?: FlightLogEntry[];
  recentRouteSettings?: Partial<RecentRouteSettings> | null;
  /** Per-pilot area/distance prefs — soft mint hints only. */
  crewPrefs?: Array<Partial<CrewRoutePrefs> & { routeAreaPrefs?: string; preferredTripNm?: number | null }>;
  /** Desk zone for "When" / "Requested STD" lines. Omitted uses the home ICAO zone, then UTC. */
  timeZone?: string;
};

export type FetchInquiriesResult =
  | { ok: true; requests: CharterRequest[]; mode: InquirySource; fallbackReason?: string }
  | { ok: false; error: string };

/** Honest toast when Grok answered but nothing survived normalize / mint gates. */
export const GROK_INBOX_UNUSABLE =
  "Grok returned no usable inquiries after parse and mint gates.";

/** Filters ate every Grok row — not an API-key failure. */
export const GROK_INBOX_FILTERS_EMPTIED =
  "Mint filters emptied the Grok set (remint memory, Content.xml, or route cool-down).";

export { LARGE_ALLOWLIST_ICAO_FLOOR };

export const GROK_INBOX_EMPTY_AFTER_RETRY =
  "Grok returned an empty inquiries list after retry.";

export const GROK_INBOX_PARSE_AFTER_RETRY =
  "Grok reply was not valid JSON after retry.";

/** Idle / on-open mint uses the cheap tier. Max 2 Grok HTTP calls per tick. */
export const GROK_IDLE_INBOX_MODEL = XAI_INBOX_MODEL_FALLBACK;

/** Manual Fetch: one primary call + at most one parse / empty / soft-fail retry. */
export const GROK_INBOX_CALL_BUDGET_FULL = 2;
/** Idle / on-open: first cheap call + one retry. */
export const GROK_INBOX_CALL_BUDGET_IDLE = 2;

/** xAI structured-output schema — non-empty inquiries when Destinations allow it. */
export const GROK_INBOX_JSON_SCHEMA = {
  type: "object",
  additionalProperties: false,
  required: ["inquiries"],
  properties: {
    inquiries: {
      type: "array",
      minItems: 1,
      items: {
        type: "object",
        additionalProperties: true,
        required: ["originIcao", "destIcao", "paxCount"],
        properties: {
          clientName: { type: "string" },
          clientEmail: { type: "string" },
          clientCompany: { type: "string" },
          originIcao: { type: "string" },
          destIcao: { type: "string" },
          origin: { type: "string" },
          destination: { type: "string" },
          departureIso: { type: "string" },
          paxCount: { type: "integer" },
          passengers: { type: "integer" },
          cargoLb: { type: "integer" },
          cargo: { type: "integer" },
          reason: { type: "string" },
          specialRequests: { type: "string" },
          notes: { type: "string" },
          flightType: { type: "string" },
          type: { type: "string" },
          seasonalPurpose: { type: "string" },
          tone: { type: "string" },
          emailSubject: { type: "string" },
          emailBody: { type: "string" },
          suggestedAircraft: { type: "string" },
          suggestedAircraftId: { type: "string" },
          aircraftId: { type: "string" },
          requestedAircraft: { type: "string" },
          arriveByIso: { type: "string" },
          mxPositioning: { type: "boolean" },
          itineraryKind: { type: "string" },
          viaIcao: { type: "string" },
          techStopIcao: { type: "string" },
        },
      },
    },
  },
} as const;

export function grokInboxCallBudget(kind?: FetchInquiriesInput["inboxPromptKind"]): number {
  return kind === "idle" ? GROK_INBOX_CALL_BUDGET_IDLE : GROK_INBOX_CALL_BUDGET_FULL;
}

export function grokInboxResponseFormat(kind: GrokInboxResponseFormat = "json_schema"): {
  type: string;
  json_schema?: { name: string; strict: boolean; schema: typeof GROK_INBOX_JSON_SCHEMA };
} {
  if (kind === "json_object") return { type: "json_object" };
  return {
    type: "json_schema",
    json_schema: {
      name: "inbox_inquiries",
      strict: true,
      schema: GROK_INBOX_JSON_SCHEMA,
    },
  };
}

export type DraftFromRequestResult =
  | { ok: true; plan: FlightPlanDraft }
  | { ok: false; error: string };

function newId(prefix: string, now: Date): string {
  return `${prefix}-${now.getTime().toString(36)}-${Math.floor(Math.random() * 1e4).toString(36)}`;
}

function stdRngFor(input: FetchInquiriesInput, now: Date, salt = 0): () => number {
  const seed = input.seed != null && Number.isFinite(input.seed) ? input.seed : seedFromDate(now, salt);
  return mulberry32((Math.trunc(seed) + 0x51ed + salt) >>> 0);
}

/** Later STD when the aircraft is airborne so generation stays realistic. Occasional overlap is OK. */
function departureForDesk(input: FetchInquiriesInput, now: Date, rng?: () => number): Date {
  return pickInboxDeparture({
    now,
    aircraftState: input.aircraftState,
    dutyStandby: input.dutyStandby,
    rng: rng ?? stdRngFor(input, now),
  });
}

function candidateSpecForOrigin(input: FetchInquiriesInput): AircraftSpec | undefined {
  const fleet = input.fleet.filter((a) => isCharterAssignable(a));
  const rated = ratedFleet(fleet, input.ratedIcaoTypes);
  return (rated.length ? rated : fleet)[0];
}

/** Assigned / candidate tail base, else company primary. Ignores parked / live nearest. */
function originHintFor(input: FetchInquiriesInput, now: Date, spec?: AircraftSpec) {
  const candidate = spec ?? candidateSpecForOrigin(input);
  return inboxOriginIcao({
    scenery: input.scenery,
    homeIcao: input.homeIcao,
    airframeBaseIcao: candidate ? airframeBaseIcao(candidate, { homeIcao: input.homeIcao, extraBases: input.extraBases }) : undefined,
    now,
  });
}

/** Desk clock for generated mail. Never assumes Halifax when the desk has no zone. */
function inboxClockZone(input: { timeZone?: string; homeIcao?: string }): string {
  const explicit = input.timeZone?.trim();
  if (explicit) return explicit;
  return timeZoneForIcao(input.homeIcao) || "UTC";
}

function deskBasesOf(input: FetchInquiriesInput): string[] {
  return liveDeskBases({ homeIcao: input.homeIcao, extraBases: input.extraBases });
}

function originPriorityFor(input: FetchInquiriesInput, spec?: AircraftSpec): string[] {
  const bases = companyOriginIcaos({ homeIcao: input.homeIcao, extraBases: input.extraBases }, spec);
  const parked = spec?.lastAirportIcao;
  if (!parkedFieldNearBases(parked, bases, input.scenery)) return bases;
  const code = parked!.trim().toUpperCase();
  return [code, ...bases.filter((icao) => icao !== code)];
}

function mintPreferFor(
  input: FetchInquiriesInput,
  purpose: SeasonalPurposeId,
  month: number,
  packPrefer: string[],
  scanPrefer: string[],
): string[] {
  const bases = deskBasesOf(input);
  return softPreferIcaos({
    purposeDests: [...preferDestsForPurpose(purpose, month, bases), ...destsForPurpose(purpose)],
    packIcaos: packPrefer,
    scanIcaos: scanPrefer,
    bases,
    scenery: input.scenery,
  });
}

function uniqueIcaos(codes: Array<string | undefined | null>): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of codes) {
    const icao = raw?.trim().toUpperCase();
    if (!icao || seen.has(icao)) continue;
    seen.add(icao);
    out.push(icao);
  }
  return out;
}

/**
 * Company / tail bases only. Destinations scenery is never an origin candidate
 * unless that ICAO is home, an extra base, or the assigned airframe base.
 */
function originSearchOrder(
  scenery: SceneryAirport[],
  preferOriginIcao: string | undefined,
  rng: () => number,
  extraPriority: string[] = [],
): SceneryAirport[] {
  const priority = uniqueIcaos([preferOriginIcao, ...extraPriority]);
  const preferred = priority
    .map(
      (icao) =>
        scenery.find((s) => s.icao.toUpperCase() === icao) ??
        hydrateSceneryAirport(icao, scenery) ??
        hydrateSceneryAirport(icao, []),
    )
    .filter((row): row is SceneryAirport => Boolean(row));
  if (preferred.length > 1 && rng() > 0.68) {
    const [first, ...tail] = preferred;
    return [...tail, first!];
  }
  return preferred;
}

function routeParty(
  input: Pick<FetchInquiriesInput, "homeIcao" | "operatorName">,
  originIcao: string,
  destIcao: string,
  rng?: () => number,
) {
  return regionalParty({
    originIcao,
    destIcao,
    homeIcao: input.homeIcao,
    operatorName: input.operatorName,
    rng,
  });
}

function cargoCapLb(spec: AircraftSpec, paxCount: number): number {
  return cargoCapFromPayload(spec, paxCount);
}

export function routeDistanceNm(
  origin: SceneryAirport,
  dest: SceneryAirport,
  scenery: SceneryAirport[],
): number | null {
  const oc = resolveCoords(origin.icao, [origin, ...scenery]);
  const dc = resolveCoords(dest.icao, [dest, ...scenery]);
  if (!oc || !dc) return null;
  return haversineNm(oc.lat, oc.lon, dc.lat, dc.lon);
}

export function suitableAircraft(
  fleet: AircraftSpec[],
  origin: SceneryAirport,
  dest: SceneryAirport,
  paxCount: number,
  cargoLb = 0,
  scenery: SceneryAirport[] = [],
): AircraftSpec[] {
  const nm = routeDistanceNm(origin, dest, scenery);
  if (nm == null) return [];
  return fleet.filter((spec) => {
    if (!isCharterAssignable(spec)) return false;
    if (paxCount > spec.maxPax) return false;
    if (cargoLb > cargoCapLb(spec, paxCount)) return false;
    if (jetRejectsCharterStage(spec, origin.icao, dest.icao, nm)) return false;
    return validForAircraft(spec, dest.runwayFt, nm, false);
  });
}

/** Suitability using proposed hops (tech-stop / multi-stop) instead of the direct city-pair. */
export function suitableAircraftForRequest(
  fleet: AircraftSpec[],
  request: Pick<CharterRequest, "originIcao" | "destIcao" | "paxCount" | "cargoLb" | "proposedLegs" | "itineraryKind" | "techStopPending">,
  scenery: SceneryAirport[] = [],
): AircraftSpec[] {
  const hops = payingOrTechStopLegs(request.proposedLegs).filter((leg) => leg.destIcao !== "TBD" && leg.originIcao !== "TBD");
  if (hops.length > 1 && request.itineraryKind === "tech_stop") {
    return fleet.filter((spec) => {
      if (!isCharterAssignable(spec)) return false;
      if (request.paxCount > spec.maxPax) return false;
      if (request.cargoLb > cargoCapLb(spec, request.paxCount)) return false;
      return hops.every((leg) => {
        const origin = hydrateSceneryAirport(leg.originIcao, scenery);
        const dest = hydrateSceneryAirport(leg.destIcao, scenery);
        if (!origin || !dest) return false;
        const nm = hopDistanceNm(origin.icao, dest.icao, scenery);
        if (nm == null) return false;
        if (jetRejectsCharterStage(spec, origin.icao, dest.icao, nm)) return false;
        return validForAircraft(spec, dest.runwayFt, nm, false);
      });
    });
  }
  const origin = hydrateSceneryAirport(request.originIcao, scenery);
  const dest = hydrateSceneryAirport(request.destIcao, scenery);
  if (!origin || !dest) return [];
  return suitableAircraft(fleet, origin, dest, request.paxCount, request.cargoLb, scenery);
}

function preferSet(input?: string[]): Set<string> {
  return new Set((input ?? []).map((c) => c.toUpperCase()));
}

function destRank(icao: string, prefer: Set<string>, purpose?: string): number {
  return destPreferenceRank(icao, { purpose, prefer });
}

function mintContextOf(input: FetchInquiriesInput, now?: Date): MintAvoidance {
  return mintAvoidanceFromDesk({
    now: now ?? input.now ?? new Date(),
    settings: input.recentRouteSettings ?? {
      deprioritizeRecentRoutes: true,
      recentRouteCooldownDays: 2,
    },
    inboxRouteMemory: input.inboxRouteMemory,
    flightLogs: input.flightLogs,
    missions: input.boardMissions as import("./types").Mission[] | undefined,
    scenery: input.scenery,
  });
}

/** Small stable jitter so equal-gravity fields don't follow scenery order. */
function destTieBreak(icao: string): number {
  let hash = 0;
  for (let i = 0; i < icao.length; i += 1) hash = (hash * 33 + icao.charCodeAt(i)) >>> 0;
  return (hash % 300) / 100;
}

function findLegalDestFromOrigin(
  origin: SceneryAirport,
  scenery: SceneryAirport[],
  fleet: AircraftSpec[],
  paxCount: number,
  cargoLb: number,
  preferIcaos?: string[],
  allowUninstalled = true,
  rng: () => number = Math.random,
  purpose?: string,
  avoidance?: MintAvoidance,
  batchRegions?: Set<string>,
  bases: string[] = [],
): { dest: SceneryAirport; spec: AircraftSpec } | null {
  const prefer = preferSet(preferIcaos);
  const legal: Array<{ dest: SceneryAirport; spec: AircraftSpec; score: number; hard: boolean }> = [];
  for (const rawDest of scenery) {
    if (rawDest.icao.toUpperCase() === origin.icao.toUpperCase()) continue;
    if (prefer.size && !allowUninstalled && !prefer.has(rawDest.icao.toUpperCase())) continue;
    const dest = hydrateSceneryAirport(rawDest.icao, scenery);
    if (!dest) continue;
    const fits = suitableAircraft(fleet, origin, dest, paxCount, cargoLb, scenery);
    if (!fits.length) continue;
    const nm = hopNm(origin.icao, dest.icao, scenery);
    const penalty = avoidance
      ? mintDestPenalty(origin.icao, dest.icao, avoidance, { nm, batchRegions })
      : 0;
    const rank = destRank(dest.icao, prefer, purpose);
    const gravity = destGravityPenalty(dest.icao, bases, scenery);
    // Weight 2 keeps a one-step theater penalty (Canada from a US base, ~6)
    // inside the occasional stretch band and outside the home band. A heavier
    // weight made every non-zero penalty unreachable, so Classic never left
    // the nearest same-theater fields. Tie-break is stable so scoring does
    // not consume the mint seed once per scenery row.
    legal.push({
      dest,
      spec: fits[0]!,
      score: gravity * 2 + rank + penalty + destTieBreak(dest.icao),
      hard: Boolean(
        avoidance && shouldExcludeMintPair(origin.icao, dest.icao, avoidance, scenery.length - 1),
      ),
    });
  }
  const pool = legal.filter((row) => !row.hard);
  const pickFrom = pool.length ? pool : legal;
  if (!pickFrom.length) return null;
  pickFrom.sort((a, b) => a.score - b.score);
  const best = pickFrom[0]!.score;
  const homeBand = pickFrom.filter((row) => row.score <= best + 4);
  const stretch = pickFrom.filter((row) => row.score > best + 4 && row.score <= best + 18);
  const band = stretch.length && rng() < 0.18 ? stretch : homeBand;
  const top = band.slice(0, Math.min(6, band.length));
  const choice = top[Math.floor(rng() * top.length)] ?? top[0]!;
  return { dest: choice.dest, spec: choice.spec };
}

function findLegalPair(
  scenery: SceneryAirport[],
  fleet: AircraftSpec[],
  paxCount: number,
  cargoLb: number,
  preferIcaos?: string[],
  allowUninstalled = true,
  preferOriginIcao?: string,
  rng: () => number = Math.random,
  purpose?: string,
  avoidance?: MintAvoidance,
  batchRegions?: Set<string>,
  extraOriginPriority: string[] = [],
  bases: string[] = [],
): { origin: SceneryAirport; dest: SceneryAirport; spec: AircraftSpec } | null {
  const homeFirst = originSearchOrder(scenery, preferOriginIcao, rng, extraOriginPriority);
  for (const rawOrigin of homeFirst) {
    const origin = hydrateSceneryAirport(rawOrigin.icao, scenery);
    if (!origin) continue;
    const hit = findLegalDestFromOrigin(
      origin,
      scenery,
      fleet,
      paxCount,
      cargoLb,
      preferIcaos,
      allowUninstalled,
      rng,
      purpose,
      avoidance,
      batchRegions,
      bases,
    );
    if (hit) return { origin, dest: hit.dest, spec: hit.spec };
  }
  return null;
}

function techStopListsFromInput(input: FetchInquiriesInput) {
  const base = effectiveTechStopAllowlist({
    imported: input.techStopIcaos ?? [],
    inventory: input.techStopInventory ?? input.preferIcaos ?? [],
  });
  if (!input.scheduleOnlyContentXmlAirports) return base;
  const allowed = new Set(inventoryIcaosFromPrefers(input));
  return base.filter((icao) => allowed.has(icao.toUpperCase()));
}

function withScheduledScenery(input: FetchInquiriesInput): FetchInquiriesInput | { error: string } {
  const inventory = inventoryIcaosFromPrefers(input);
  const prepared = applyContentXmlScheduleToScenery(
    input.scenery,
    Boolean(input.scheduleOnlyContentXmlAirports),
    inventory,
  );
  if (!prepared.ok) return { error: prepared.error };
  if (!input.scheduleOnlyContentXmlAirports) return { ...input, scenery: prepared.scenery };
  return {
    ...input,
    scenery: prepared.scenery,
    preferIcaos: inventory,
    sceneryGateEnabled: true,
    allowUninstalled: false,
  };
}

function contentXmlLegalInquiries(rows: CharterRequest[], input: FetchInquiriesInput): CharterRequest[] {
  const inventory = input.scheduleOnlyContentXmlAirports ? inventoryIcaosFromPrefers(input) : null;
  if (!inventory) return rows;
  return rows.filter((row) => !contentXmlScheduleBlocked(true, inventory, icaosFromRoute(row)));
}

function allowlistIsLarge(input: FetchInquiriesInput): boolean {
  return (input.scenery?.length ?? 0) >= LARGE_ALLOWLIST_ICAO_FLOOR;
}

/** Content.xml stays a hard gate. Remint / cool-down are hard only until they would wipe a large allow-list set. */
function dropBlockedInquiries(
  rows: CharterRequest[],
  input: FetchInquiriesInput,
  remint: "hard" | "soft" = "hard",
): CharterRequest[] {
  const contentOk = contentXmlLegalInquiries(rows, input);
  if (remint === "soft") return contentOk;
  const avoidance = mintContextOf(input);
  return contentOk.filter((row) => !requestMatchesMintAvoidance(row, avoidance));
}

function withRunwayPicWarning(row: CharterRequest, scenery: SceneryAirport[]): CharterRequest {
  const origin = hydrateSceneryAirport(row.originIcao, scenery);
  const dest = hydrateSceneryAirport(row.destIcao, scenery);
  const note = origin && dest
    ? runwayPicPairWarning(
        { icao: origin.icao, runwayFt: origin.runwayFt },
        { icao: dest.icao, runwayFt: dest.runwayFt },
      )
    : null;
  if (!note) return row;
  return { ...row, warning: [row.warning, note].filter(Boolean).join(" ") };
}

function findLongRangePair(
  scenery: SceneryAirport[],
  fleet: AircraftSpec[],
  paxCount: number,
  cargoLb: number,
  allowlist: string[],
  preferOriginIcao?: string,
  extraDests: SceneryAirport[] = [],
  rng: () => number = Math.random,
  avoidance?: MintAvoidance,
  extraOriginPriority: string[] = [],
  bases: string[] = [],
): { origin: SceneryAirport; dest: SceneryAirport; spec: AircraftSpec } | null {
  if (!allowlist.length) return null;
  const destPool = [...scenery, ...extraDests];
  const homeFirst = originSearchOrder(scenery, preferOriginIcao, rng, extraOriginPriority);
  const dests = [...destPool].sort((a, b) => {
    const ga = destGravityPenalty(a.icao, bases, scenery);
    const gb = destGravityPenalty(b.icao, bases, scenery);
    if (ga !== gb) return ga - gb;
    if (!avoidance) return rng() - 0.5;
    const pa = mintDestPenalty("HOME", a.icao, avoidance);
    const pb = mintDestPenalty("HOME", b.icao, avoidance);
    return pa !== pb ? pa - pb : rng() - 0.5;
  });
  for (const rawOrigin of homeFirst) {
    const origin = hydrateSceneryAirport(rawOrigin.icao, scenery);
    if (!origin) continue;
    for (const rawDest of dests) {
      if (rawDest.icao.toUpperCase() === origin.icao.toUpperCase()) continue;
      if (avoidance && shouldExcludeMintPair(origin.icao, rawDest.icao, avoidance, dests.length - 1)) continue;
      const dest = hydrateSceneryAirport(rawDest.icao, [...scenery, ...extraDests]);
      if (!dest) continue;
      const nm = routeDistanceNm(origin, dest, [...scenery, dest]);
      if (nm == null) continue;
      const spec = fleet.find((s) => {
        if (!isCharterAssignable(s)) return false;
        if (paxCount > s.maxPax) return false;
        if (cargoLb > cargoCapLb(s, paxCount)) return false;
        if (jetRejectsCharterStage(s, origin.icao, dest.icao, nm)) return false;
        return needsTechStop(nm, s);
      });
      if (!spec) continue;
      const plan = planTechStopRoute({
        origin,
        dest,
        spec,
        paxCount,
        scenery,
        allowlist,
      });
      if (plan.needed && !plan.pending && plan.stopIcao) {
        return { origin, dest, spec };
      }
    }
  }
  return null;
}

function coerceRoute(
  originIcao: string,
  destIcao: string,
  scenery: SceneryAirport[],
  fleet: AircraftSpec[],
  paxCount: number,
  cargoLb: number,
  homeIcao?: string,
  extraBases?: string[],
): { origin: SceneryAirport; dest: SceneryAirport; warning?: string; rerouted: boolean } | null {
  const allow = new Set(scenery.map((s) => s.icao.toUpperCase()));
  let origin = hydrateSceneryAirport(originIcao, scenery);
  let dest = hydrateSceneryAirport(destIcao, scenery);
  let warning: string | undefined;
  const origins = companyOriginIcaos({ homeIcao, extraBases });
  const gravityBases = origins;

  if (!origin || !allow.has(origin.icao.toUpperCase())) {
    const homeCode = origins[0] || "";
    const home =
      (homeCode
        ? scenery.find((s) => s.icao.toUpperCase() === homeCode) ??
          hydrateSceneryAirport(homeCode, scenery) ??
          hydrateSceneryAirport(homeCode, [])
        : undefined) ?? scenery[0];
    origin = home ? hydrateSceneryAirport(home.icao, scenery) ?? hydrateSceneryAirport(home.icao, []) : undefined;
    warning = "Origin remapped to company home / tail base.";
  }
  if (!origin) return null;
  const resolvedOrigin = origin;

  let destFits = false;
  let destNeedsStop = false;
  if (dest && allow.has(dest.icao.toUpperCase()) && dest.icao.toUpperCase() !== resolvedOrigin.icao.toUpperCase()) {
    const resolvedDest = dest;
    destFits = suitableAircraft(fleet, resolvedOrigin, resolvedDest, paxCount, cargoLb, scenery).length > 0;
    destNeedsStop = fleet.some((spec) => {
      const nm = routeDistanceNm(resolvedOrigin, resolvedDest, scenery);
      return nm != null && needsTechStop(nm, spec);
    });
  }
  const destOk = destFits || destNeedsStop;

  if (!destOk) {
    const fromOrigin = findLegalDestFromOrigin(
      origin,
      scenery,
      fleet,
      paxCount,
      cargoLb,
      undefined,
      true,
      Math.random,
      undefined,
      undefined,
      undefined,
      gravityBases,
    );
    if (fromOrigin) {
      dest = fromOrigin.dest;
    } else {
      const any = findLegalPair(
        scenery,
        fleet,
        Math.min(paxCount, 4),
        0,
        undefined,
        true,
        undefined,
        Math.random,
        undefined,
        undefined,
        undefined,
        gravityBases,
        gravityBases,
      );
      if (!any) return null;
      origin = any.origin;
      dest = any.dest;
    }
    warning = warning
      ? `${warning} Destination remapped to a legal field.`
      : "Destination remapped to a legal field on the allow-list.";
  }

  if (!dest) return null;
  return { origin, dest, warning, rerouted: Boolean(warning) };
}

function missionCallsign(spec: AircraftSpec, company?: RadioCompany | null): string {
  return radioIdFor(spec, company);
}

function storyFor(
  type: FlightType,
  client: string,
  firm: string,
  origin: SceneryAirport,
  dest: SceneryAirport,
  paxCount: number,
  cargoLb: number,
  special: string,
  month?: number,
  purposeId?: SeasonalPurposeId,
): { purpose: string; scenario: string; reason: string } {
  const cargoBit = cargoLb > 0 ? ` Cargo ${cargoLb} lb.` : "";
  const extra = special ? ` ${special}` : "";
  const purpose = purposeId ?? purposeFromFlightType(type, month ?? 7, dest.icao, origin.icao);
  const atlanticGolf = isAtlanticGolfRegion(origin.icao) || isAtlanticGolfRegion(dest.icao);
  const golfOutbound = purpose === "golf" && isAtlanticGolfRegion(origin.icao) && !isAtlanticGolfRegion(dest.icao);
  if (golfOutbound) {
    return {
      purpose: "Golf return / outbound",
      reason: `${client} (${firm}) lifting off after the round.`,
      scenario: `${client} + party of ${paxCount} ${origin.name}–${dest.name}. Clubs in the aft bay.${cargoBit}${extra}`,
    };
  }
  if (purpose === "golf" && !atlanticGolf) {
    return {
      purpose: "Golf outing",
      reason: `${client} (${firm}) golf lift ${origin.name} to ${dest.name}.`,
      scenario: `${client} + party of ${paxCount} ${origin.name}–${dest.name}. Clubs in the aft bay.${cargoBit}${extra}`,
    };
  }
  const packed = purposeReason(purpose, client, firm, origin.name, dest.name, paxCount);
  return {
    purpose: packed.purpose,
    reason: packed.reason,
    scenario: `${packed.scenario}${cargoBit}${extra}`,
  };
}

function composeEmail(input: {
  client: string;
  firm: string;
  email: string;
  origin: SceneryAirport;
  dest: SceneryAirport;
  departure: Date;
  paxCount: number;
  cargoLb: number;
  reason: string;
  special: string;
  type: FlightType;
  tone: InquiryTone;
  operatorName: string;
  timeZone?: string;
}): { subject: string; body: string } {
  const when = formatInboxStdLine(input.departure, input.timeZone?.trim() || "UTC");
  const desk = input.operatorName;
  const route = `${input.origin.icao}–${input.dest.icao}`;
  if (input.tone === "casual") {
    const subject = pick([
      `Any chance of a lift ${input.origin.icao} to ${input.dest.icao}?`,
      `Quick ask — ${route} this week`,
      `Hi ${desk.split(" ")[0] ?? "there"} — need a plane`,
    ]);
    const opener = pick([
      `Hi — I'm ${input.client}. Hoping you can fly us ${input.origin.name} over to ${input.dest.name}.`,
      `Hey ${desk} desk, ${input.client} here. Can you do ${route}?`,
      `Morning — ${input.client} with ${input.firm}. We need a last-minute hop.`,
    ]);
    const body = [
      `From: ${input.client} <${input.email}>`,
      "",
      opener,
      "",
      `${input.origin.icao} → ${input.dest.icao}`,
      `When: ${when}`,
      `People: ${input.paxCount}${input.cargoLb ? ` + ${input.cargoLb} lb bags` : ""}`,
      input.special ? `Also: ${input.special}` : "",
      "",
      input.type === "medical"
        ? "It's medical — sooner the better. Text me if that's easier."
        : "Not picky on the tail. Just something comfortable. Thanks!",
      "",
      input.client,
    ]
      .filter((line) => line !== "")
      .join("\n");
    return { subject, body };
  }

  const subject = `RFQ — ${route} — ${input.paxCount} pax — ${input.client}`;
  const body = [
    `From: ${input.client} <${input.email}>`,
    `Company: ${input.firm}`,
    "",
    `Good afternoon ${desk} Dispatch,`,
    "",
    `On behalf of ${input.firm} I am requesting a charter quotation ${input.origin.icao} (${input.origin.name}) to ${input.dest.icao} (${input.dest.name}).`,
    "",
    `Requested STD: ${when}`,
    `Souls on board: ${input.paxCount}`,
    `Declared cargo: ${input.cargoLb} lb`,
    `Nature of flight: ${input.reason}`,
    input.special ? `Handling notes: ${input.special}` : "Handling notes: none at this time.",
    "",
    input.type === "medical"
      ? "This is time-sensitive medical traffic. Please confirm the first available legal slot and send the quote to this address."
      : "Please confirm aircraft, block time, and a firm quote at your earliest convenience.",
    "",
    `Respectfully,`,
    input.client,
    `${input.firm} — charter brokerage`,
  ].join("\n");
  return { subject, body };
}

function appendEmailNote(body: string, note: string): string {
  const extra = note.trim();
  if (!extra) return body;
  return `${body.trimEnd()}\n\n${extra}`;
}

function applyItineraryPlan(
  row: CharterRequest,
  plan: ReturnType<typeof planOutboundItinerary>,
): CharterRequest {
  const note = itineraryNoteForEmail(plan);
  const lastPaying = payingProposedLegs(plan.proposedLegs).at(-1);
  return {
    ...row,
    destIcao: lastPaying?.destIcao ?? row.destIcao,
    destName: lastPaying?.destName ?? row.destName,
    itineraryKind: plan.kind,
    returnEmpty: plan.returnEmpty,
    proposedLegs: plan.proposedLegs,
    specialRequests: [row.specialRequests, note].filter(Boolean).join(" "),
    emailBody: appendEmailNote(row.emailBody, note),
  };
}

function buildBackhaulCompanion(
  outbound: CharterRequest,
  input: FetchInquiriesInput,
  rng: () => number,
): CharterRequest | null {
  if (outbound.mxPositioning || outbound.itineraryKind === "backhaul") return null;
  if (
    !outbound.returnEmpty &&
    outbound.itineraryKind !== "round_trip_empty" &&
    outbound.itineraryKind !== "multi_stop" &&
    outbound.itineraryKind !== "tech_stop"
  ) {
    return null;
  }
  if (!shouldOfferBackhaul(rng)) return null;
  const origin = hydrateSceneryAirport(outbound.destIcao, input.scenery);
  const dest = hydrateSceneryAirport(outbound.originIcao, input.scenery);
  if (!origin || !dest) return null;
  const spec =
    input.fleet.find((a) => a.id === outbound.suggestedAircraftId) ??
    suitableAircraft(input.fleet, origin, dest, Math.max(1, outbound.paxCount), outbound.cargoLb, input.scenery)[0];
  if (!spec) return null;
  const nm = routeDistanceNm(origin, dest, input.scenery);
  if (nm == null) return null;
  const eteMin = eteMinutes(nm, planningCruiseKt(spec, nm));
  const outboundDep = new Date(outbound.departureIso);
  const departure = Number.isNaN(outboundDep.getTime())
    ? departureForDesk(input, input.now ?? new Date())
    : new Date(backhaulDepartureIso(outboundDep, eteMin));
  const party = routeParty(input, origin.icao, dest.icao, rng);
  const client = party.clientName;
  const firm = party.clientCompany;
  const email = party.clientEmail;
  const month = halifaxMonth(input.now ?? new Date());
  const paxCount = clampCharterPax(1 + Math.floor(rng() * Math.max(1, Math.min(4, spec.maxPax))), spec);
  const cargoLb = Math.floor(rng() * 160);
  const tone: InquiryTone = "broker";
  const note = backhaulEmailNote();
  const story = storyFor(outbound.flightType === "ferry" ? "corporate" : outbound.flightType, client, firm, origin, dest, paxCount, cargoLb, note, month);
  const mail = composeEmail({
    client,
    firm,
    email,
    origin,
    dest,
    departure,
    paxCount,
    cargoLb,
    reason: `${story.reason} Reverse empty-leg fill.`,
    special: note,
    type: outbound.flightType === "ferry" ? "corporate" : outbound.flightType,
    tone,
    operatorName: input.operatorName?.trim() || SUGGESTED_COMPANY_NAME,
    timeZone: inboxClockZone(input),
  });
  const backhaulId = newId("inq", input.now ?? new Date());
  return {
    id: backhaulId,
    createdAt: (input.now ?? new Date()).toISOString(),
    source: outbound.source,
    status: "new",
    clientName: client,
    clientEmail: email,
    clientCompany: firm,
    originIcao: origin.icao,
    destIcao: dest.icao,
    originName: origin.name,
    destName: dest.name,
    departureIso: departure.toISOString(),
    paxCount,
    cargoLb,
    reason: `${story.reason} Backhaul fill — discounted vs outbound ${outbound.originIcao}–${outbound.destIcao}.`,
    specialRequests: note,
    emailSubject: `Backhaul fill — ${origin.icao}–${dest.icao} — ${client}`,
    emailBody: appendEmailNote(mail.body, note),
    suggestedAircraftId: spec.id,
    flightType: outbound.flightType === "ferry" ? "corporate" : outbound.flightType,
    seasonalPurpose: outbound.seasonalPurpose,
    tone,
    itineraryKind: "backhaul",
    linkedRequestId: outbound.id,
    quoteDiscount: BACKHAUL_QUOTE_FACTOR,
    returnEmpty: false,
    proposedLegs: [
      {
        originIcao: origin.icao,
        destIcao: dest.icao,
        originName: origin.name,
        destName: dest.name,
        paxCount,
        notes: "Paying reverse / backhaul fill.",
      },
    ],
  };
}

function decorateGeneratedInquiries(
  rows: CharterRequest[],
  input: FetchInquiriesInput,
  rng: () => number,
  allowCompanion = true,
  keepPendingTechStop = false,
): CharterRequest[] {
  const home = input.homeIcao || "";
  const decorated: CharterRequest[] = [];
  for (const row of rows) {
    if (row.mxPositioning) {
      decorated.push(row);
      continue;
    }
    const origin = hydrateSceneryAirport(row.originIcao, input.scenery);
    const dest = hydrateSceneryAirport(row.destIcao, input.scenery);
    const spec = input.fleet.find((a) => a.id === row.suggestedAircraftId);
    if (!origin || !dest || !spec) {
      decorated.push(row);
      continue;
    }
    const nm = routeDistanceNm(origin, dest, input.scenery);
    const allowlist = techStopListsFromInput(input);
    if (nm != null && needsTechStop(nm, spec)) {
      const tech = planTechStopRoute({
        origin,
        dest,
        spec,
        paxCount: row.paxCount,
        scenery: input.scenery,
        allowlist,
        homeIcao: home,
        manualIcao: row.techStopIcao,
        mxPositioning: row.mxPositioning,
      });
      if (tech.needed && !tech.pending) {
        const outbound = applyTechStopPlanToRequest(row, tech);
        const companion = allowCompanion ? buildBackhaulCompanion(outbound, input, rng) : null;
        if (companion) {
          decorated.push({ ...outbound, linkedRequestId: companion.id }, companion);
        } else {
          decorated.push(outbound);
        }
        continue;
      }
      if (tech.needed && tech.pending) {
        if (keepPendingTechStop) {
          decorated.push(applyTechStopPlanToRequest(row, tech));
        }
        continue;
      }
    }
    const plan = planOutboundItinerary({
      origin,
      dest,
      spec,
      paxCount: row.paxCount,
      homeIcao: home,
      scenery: input.scenery,
      rng,
      mxPositioning: row.mxPositioning,
    });
    const outbound = applyItineraryPlan(row, plan);
    const companion = allowCompanion ? buildBackhaulCompanion(outbound, input, rng) : null;
    if (companion) {
      decorated.push({ ...outbound, linkedRequestId: companion.id }, companion);
    } else {
      decorated.push(outbound);
    }
  }
  return decorated;
}

export function generateDemoInquiries(input: FetchInquiriesInput): CharterRequest[] {
  const scheduled = withScheduledScenery(input);
  if ("error" in scheduled) return [];
  input = scheduled;
  const now = input.now ?? new Date();
  const stdRng = stdRngFor(input, now, 1);
  const count = Math.max(1, Math.min(input.count ?? 2, 4));
  const fleet = input.fleet.filter((a) => isCharterAssignable(a));
  const assignable = ratedFleet(fleet, input.ratedIcaoTypes);
  const searchFleet = assignable.length ? assignable : fleet;
  const operatorName = input.operatorName?.trim() || SUGGESTED_COMPANY_NAME;
  const out: CharterRequest[] = [];
  const usedRoutes = new Set<string>();
  const tones: InquiryTone[] = count >= 2 ? ["broker", "casual"] : [pick(["broker", "casual"] as const)];
  const avoidance = mintContextOf(input, now);
  const batchRegions = new Set<string>();

  const month = halifaxMonth(now);
  for (let i = 0; i < count * 4 && out.length < count; i += 1) {
    const purpose = pickSeasonalPurpose(month);
    const type = PURPOSE_TO_FLIGHT_TYPE[purpose];
    const specGuess = pick(searchFleet);
    const paxCount = clampCharterPax(
      type === "medical" ? 1 + Math.floor(Math.random() * 2) : 1 + Math.floor(Math.random() * specGuess.maxPax),
      specGuess,
    );
    const cargoLb = type === "medical" ? 80 + Math.floor(Math.random() * 120) : Math.floor(Math.random() * 280);
    const originHint = originHintFor(input, now, specGuess).icao;
    const bases = deskBasesOf(input);
    const pair = findLegalPair(
      input.scenery,
      searchFleet,
      paxCount,
      cargoLb,
      mintPreferFor(
        input,
        purpose,
        month,
        [],
        input.sceneryGateEnabled === false ? [] : (input.preferIcaos ?? []),
      ),
      input.allowUninstalled !== false,
      originHint,
      Math.random,
      purpose,
      avoidance,
      batchRegions,
      originPriorityFor(input, specGuess),
      bases,
    );
    if (!pair?.origin.icao || !pair.dest.icao) continue;
    const flightType = remapUnseasonalType(type, pair.origin.icao, pair.dest.icao, month);
    if (flightType === "golf" && !isGolfAllowed(pair.origin.icao, pair.dest.icao, month)) continue;
    const key = `${pair.origin.icao}-${pair.dest.icao}`;
    if (usedRoutes.has(key) && out.length) continue;
    if (shouldExcludeMintPair(pair.origin.icao, pair.dest.icao, avoidance, input.scenery.length - 1) && out.length) {
      continue;
    }
    usedRoutes.add(key);
    batchRegions.add(destRegionOf(pair.dest.icao));

    const party = routeParty(input, pair.origin.icao, pair.dest.icao, stdRng);
    const client = party.clientName;
    const firm = party.clientCompany;
    const email = party.clientEmail;
    const departure = departureForDesk(input, now, stdRng);
    const specials = [
      "",
      "Need ground transport on arrival.",
      "Cats in cabin — please confirm.",
      "Early coffee, no press.",
      flightType === "golf" ? "Golf bags × 4 in the aft bay." : "Soft bags only.",
    ];
    const special = pick(specials);
    const tone = tones[out.length % tones.length] ?? "broker";
    const story = storyFor(flightType, client, firm, pair.origin, pair.dest, paxCount, cargoLb, special, month, purpose);
    const mail = composeEmail({
      client,
      firm,
      email,
      origin: pair.origin,
      dest: pair.dest,
      departure,
      paxCount,
      cargoLb,
      reason: story.reason,
      special,
      type: flightType,
      tone,
      operatorName,
      timeZone: inboxClockZone(input),
    });

    out.push({
      id: newId("inq", now),
      createdAt: now.toISOString(),
      source: input.jobMode === "automatic" ? "automatic" : "demo",
      status: "new",
      clientName: client,
      clientEmail: email,
      clientCompany: firm,
      originIcao: pair.origin.icao,
      destIcao: pair.dest.icao,
      originName: pair.origin.name,
      destName: pair.dest.name,
      departureIso: departure.toISOString(),
      paxCount,
      cargoLb,
      reason: story.reason,
      specialRequests: special,
      emailSubject: mail.subject,
      emailBody: mail.body,
      suggestedAircraftId: pair.spec.id,
      flightType,
      seasonalPurpose: purpose,
      tone,
      warning: missingSceneryWarning(
        pair.dest.icao,
        (input.preferIcaos ?? []).map((icao) => ({
          icao,
          source: "folder_name" as const,
          scannedAtUtc: now.toISOString(),
        })),
        Boolean(input.sceneryGateEnabled && input.preferIcaos?.length),
      ),
    });
  }

  return attachDeskQuotes(decorateGeneratedInquiries(out, input, () => Math.random()), input);
}

export function mulberry32(seed: number): () => number {
  let t = seed >>> 0;
  return () => {
    t += 0x6d2b79f5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

export function seedFromDate(now: Date, salt = 0): number {
  return (now.getUTCFullYear() * 10_000 + (now.getUTCMonth() + 1) * 100 + now.getUTCDate() + salt * 97) >>> 0;
}

function pickRng<T>(list: readonly T[], rng: () => number): T {
  return list[Math.floor(rng() * list.length)] ?? list[0]!;
}

/**
 * Deterministic season/region fallback when no xAI key is on the desk.
 * Does not invent money — desk quotes are attached from the rate card.
 */
export function generateSeasonalInquiries(input: FetchInquiriesInput): CharterRequest[] {
  const scheduled = withScheduledScenery(input);
  if ("error" in scheduled) return [];
  input = scheduled;
  const now = input.now ?? new Date();
  const rng = mulberry32(input.seed ?? seedFromDate(now));
  const stdRng = stdRngFor(input, now, 2);
  const count = Math.max(1, Math.min(input.count ?? 2, 4));
  const fleet = input.fleet.filter((a) => isCharterAssignable(a));
  const assignable = ratedFleet(fleet, input.ratedIcaoTypes);
  const searchFleet = input.hardFilterRatings ? assignable : assignable.length ? assignable : fleet;
  if (input.hardFilterRatings && !searchFleet.length) return [];
  const operatorName = input.operatorName?.trim() || SUGGESTED_COMPANY_NAME;
  const month = halifaxMonth(now);
  const packPrefer: string[] = [];
  const scanPrefer = input.sceneryGateEnabled === false ? [] : (input.preferIcaos ?? []);
  const tones: InquiryTone[] = count >= 2 ? ["broker", "casual"] : [pickRng(["broker", "casual"] as const, rng)];
  const out: CharterRequest[] = [];
  const usedRoutes = new Set<string>();
  const avoidance = mintContextOf(input, now);
  const batchRegions = new Set<string>();

  const mxOpp = findMxPayloadOpportunity({
    fleet: input.fleet,
    scenery: input.scenery,
    squawks: input.squawks,
    melItems: input.melItems,
    homeIcao: input.homeIcao,
    mxBases: input.mxBases,
    rng,
  });

  const pushRow = (row: {
    origin: SceneryAirport;
    dest: SceneryAirport;
    spec: AircraftSpec;
    type: FlightType;
    purpose: SeasonalPurposeId;
    paxCount: number;
    cargoLb: number;
    mxPositioning?: boolean;
  }) => {
    const key = `${row.origin.icao}-${row.dest.icao}`;
    if (usedRoutes.has(key) && out.length) return;
    if (shouldExcludeMintPair(row.origin.icao, row.dest.icao, avoidance, input.scenery.length - 1) && out.length) {
      return;
    }
    usedRoutes.add(key);
    batchRegions.add(destRegionOf(row.dest.icao));
    const party = routeParty(input, row.origin.icao, row.dest.icao, rng);
    const client = party.clientName;
    const firm = party.clientCompany;
    const email = party.clientEmail;
    const departure = departureForDesk(input, now, stdRng);
    const special = pickRng(
      [
        "",
        "Need ground transport on arrival.",
        "Soft bags only.",
        row.type === "golf" && isGolfSeasonMonth(month) ? "Golf bags × 4 in the aft bay." : "Early coffee, no press.",
        row.mxPositioning ? `Happy to ride the ${row.spec.registration} positioning for mx.` : "",
      ].filter((s) => s !== undefined),
      rng,
    );
    const tone = tones[out.length % tones.length] ?? "broker";
    const story = storyFor(
      row.type,
      client,
      firm,
      row.origin,
      row.dest,
      row.paxCount,
      row.cargoLb,
      special,
      month,
      row.purpose,
    );
    const mail = composeEmail({
      client,
      firm,
      email,
      origin: row.origin,
      dest: row.dest,
      departure,
      paxCount: row.paxCount,
      cargoLb: row.cargoLb,
      reason: story.reason,
      special,
      type: row.type,
      tone,
      operatorName,
      timeZone: inboxClockZone(input),
    });
    out.push({
      id: newId("inq", now),
      createdAt: now.toISOString(),
      source: input.jobMode === "automatic" ? "automatic" : "demo",
      status: "new",
      clientName: client,
      clientEmail: email,
      clientCompany: firm,
      originIcao: row.origin.icao,
      destIcao: row.dest.icao,
      originName: row.origin.name,
      destName: row.dest.name,
      departureIso: departure.toISOString(),
      paxCount: row.paxCount,
      cargoLb: row.cargoLb,
      reason: `${story.reason} ${seasonCaption(month, halifaxYear(now), deskBasesOf(input))}`,
      specialRequests: special,
      emailSubject: mail.subject,
      emailBody: mail.body,
      suggestedAircraftId: row.spec.id,
      flightType: row.type,
      seasonalPurpose: row.purpose,
      mxPositioning: row.mxPositioning,
      tone,
      destPack: undefined,
      warning: missingSceneryWarning(
        row.dest.icao,
        (input.preferIcaos ?? []).map((icao) => ({
          icao,
          source: "folder_name" as const,
          scannedAtUtc: now.toISOString(),
        })),
        Boolean(input.sceneryGateEnabled && input.preferIcaos?.length),
      ),
    });
  };

  if (mxOpp) {
    const paxCount = clampCharterPax(1 + Math.floor(rng() * Math.max(1, mxOpp.spec.maxPax)), mxOpp.spec);
    pushRow({
      origin: mxOpp.origin,
      dest: mxOpp.dest,
      spec: mxOpp.spec,
      type: "corporate",
      purpose: "mx_payload",
      paxCount,
      cargoLb: Math.floor(rng() * 200),
      mxPositioning: true,
    });
  }

  for (let i = 0; i < count * 8 && out.length < count; i += 1) {
    const purpose = pickSeasonalPurpose(month, rng);
    const type = PURPOSE_TO_FLIGHT_TYPE[purpose];
    if (!searchFleet.length) break;
    const specGuess = pickRng(searchFleet, rng);
    const paxCount = clampCharterPax(
      type === "medical" ? 1 + Math.floor(rng() * 2) : 1 + Math.floor(rng() * Math.max(1, specGuess.maxPax)),
      specGuess,
    );
    const cargoLb = type === "medical" ? 80 + Math.floor(rng() * 120) : Math.floor(rng() * 280);
    const specOrigin = originHintFor(input, now, specGuess).icao;
    const originCandidates = preferOriginsForPurpose(
      purpose,
      month,
      specOrigin,
      originPriorityFor(input, specGuess),
    ).filter((icao) =>
      input.scenery.some((s) => s.icao.toUpperCase() === icao.toUpperCase()) ||
      originPriorityFor(input, specGuess).includes(icao.toUpperCase()),
    );
    const originPrefer =
      specOrigin && rng() < 0.7
        ? specOrigin
        : originCandidates.length
          ? pickRng(originCandidates, rng)
          : specOrigin;
    const extraOriginPriority = originPriorityFor(input, specGuess);
    const bases = deskBasesOf(input);
    const allowlist = techStopListsFromInput(input);
    const preferIcaos = mintPreferFor(input, purpose, month, packPrefer, scanPrefer);
    const packDests = packPrefer
      .map((icao) => hydrateSceneryAirport(icao, input.scenery) ?? hydrateSceneryAirport(icao, []))
      .filter((row): row is SceneryAirport => Boolean(row))
      .filter((row) => destGravityPenalty(row.icao, bases, input.scenery) <= 8);
    const pair =
      rng() < TECH_STOP_TRICKLE_RATE
        ? findLongRangePair(
            input.scenery,
            searchFleet,
            paxCount,
            cargoLb,
            allowlist,
            originPrefer,
            packDests,
            rng,
            avoidance,
            extraOriginPriority,
            bases,
          ) ??
          findLegalPair(
            input.scenery,
            searchFleet,
            paxCount,
            cargoLb,
            preferIcaos,
            input.allowUninstalled !== false,
            originPrefer,
            rng,
            purpose,
            avoidance,
            batchRegions,
            extraOriginPriority,
            bases,
          )
        : findLegalPair(
            input.scenery,
            searchFleet,
            paxCount,
            cargoLb,
            preferIcaos,
            input.allowUninstalled !== false,
            originPrefer,
            rng,
            purpose,
            avoidance,
            batchRegions,
            extraOriginPriority,
            bases,
          );
    if (!pair?.origin.icao || !pair.dest.icao) continue;
    const flightType = remapUnseasonalType(type, pair.origin.icao, pair.dest.icao, month);
    if (flightType === "golf" && !isGolfAllowed(pair.origin.icao, pair.dest.icao, month)) continue;
    pushRow({
      origin: pair.origin,
      dest: pair.dest,
      spec: pair.spec,
      type: flightType,
      purpose: flightType === type ? purpose : purposeFromFlightType(flightType, month, pair.dest.icao),
      paxCount,
      cargoLb,
    });
  }

  return attachDeskQuotes(decorateGeneratedInquiries(out, input, rng), input);
}

/** Strip invented cash from Grok / template copy. Desk quote is attached separately. */
export function stripInventedMoney(text: string): string {
  return text
    .replace(/\$[\d,]+(?:\.\d{1,2})?/g, "[desk rate card]")
    .replace(/\b(?:CAD|USD|EUR|GBP)\s*[\d,]+(?:\.\d{1,2})?/gi, "[desk rate card]");
}

export function deskQuoteForRequest(
  request: CharterRequest,
  input: FetchInquiriesInput,
): DeskQuoteHint | undefined {
  if (!input.rates) return undefined;
  const origin = hydrateSceneryAirport(request.originIcao, input.scenery);
  const dest = hydrateSceneryAirport(request.destIcao, input.scenery);
  if (!origin || !dest) return undefined;
  const spec =
    input.fleet.find((a) => a.id === request.suggestedAircraftId) ??
    suitableAircraftForRequest(input.fleet, request, input.scenery)[0] ??
    suitableAircraft(input.fleet, origin, dest, request.paxCount, request.cargoLb, input.scenery)[0];
  if (!spec) return undefined;
  const paying = payingOrTechStopLegs(request.proposedLegs).filter(
    (leg) => leg.originIcao !== "TBD" && leg.destIcao !== "TBD",
  );
  let nm = routeDistanceNm(origin, dest, input.scenery);
  let eteMin = nm != null ? eteMinutes(nm, planningCruiseKt(spec, nm)) : null;
  if (paying.length > 1) {
    let totalNm = 0;
    let totalEte = 0;
    for (const leg of paying) {
      const o = hydrateSceneryAirport(leg.originIcao, input.scenery);
      const d = hydrateSceneryAirport(leg.destIcao, input.scenery);
      if (!o || !d) continue;
      const hop = routeDistanceNm(o, d, input.scenery);
      if (hop == null) continue;
      totalNm += hop;
      totalEte += eteMinutes(hop, planningCruiseKt(spec, hop));
    }
    if (totalNm > 0) {
      nm = totalNm;
      eteMin = totalEte;
    }
  }
  if (nm == null || eteMin == null) return undefined;
  const fuel = fuelPlan(spec, eteMin);
  const payingMx = isPayingMxPositioning(request);
  const emptyFerry = isEmptyFerry(request);
  const flightType =
    payingMx && request.flightType === "ferry"
      ? "corporate"
      : emptyFerry
        ? "ferry"
        : request.flightType === "ferry"
          ? "corporate"
          : request.flightType;
  const quote = quoteCharter(
    {
      id: request.id,
      createdAt: request.createdAt,
      callsign: spec.registration,
      aircraftId: spec.id,
      aircraftName: spec.name,
      registration: spec.registration,
      flightType,
      originIcao: origin.icao,
      destIcao: dest.icao,
      originName: origin.name,
      destName: dest.name,
      originCity: origin.city,
      destCity: dest.city,
      distanceNm: Math.round(nm),
      eteMin,
      fuelGal: fuel.fuelGal,
      fuelLbs: fuel.fuelLbs,
      trackTrue: 0,
      paxCount: request.paxCount,
      paxNames: [],
      cargoLb: request.cargoLb,
      scenario: request.reason,
      purpose: request.reason,
      departureIso: request.departureIso,
      revenueFactor: request.quoteDiscount,
      weatherNote: "",
      originMetar: "",
      destMetar: "",
      status: "pending",
      isMaintenance: emptyFerry && destIsMroForType(dest.icao, spec.icaoType, input.mxBases),
      mxPositioning: payingMx,
      originRunwayFt: origin.runwayFt,
      destRunwayFt: dest.runwayFt,
      legs: [],
      source: "inbox",
    },
    input.rates,
    undefined,
    { catalogs: input.catalogs, aircraft: spec, opsDesk: input.opsDesk },
  );
  const followOn = findFollowOnNearDest({
    destIcao: dest.icao,
    scenery: input.scenery,
    inbox: input.boardInbox ?? [],
    missions: input.boardMissions,
    skipRequestId: request.id,
  });
  const baseCharterCad = isRevenueCharter(request) ? quote.revenueCad : 0;
  const fee = quoteFeeBreakdown({
    baseCharterCad,
    spec,
    destIcao: dest.icao,
    homeIcao: input.homeIcao,
    originIcao: origin.icao,
    scenery: input.scenery,
    rates: input.rates,
    catalogs: input.catalogs,
    returnEmpty: Boolean(request.returnEmpty) && baseCharterCad > 0,
    mxPositioning: payingMx,
    itineraryKind: request.itineraryKind,
    followOn,
    now: input.now,
  });
  const note = emptyFerry
    ? "Company ferry — no charter revenue"
    : request.itineraryKind === "backhaul"
      ? "Desk rate card — backhaul fill (discounted; paying charter)"
      : payingMx
        ? "Mx positioning charter — cheaper-than-usual desk rate card"
        : fee.repositionCad > 0
          ? fee.note
          : request.returnEmpty
            ? "Desk rate card — not a Grok figure · empty return already in the offered fee"
            : "Desk rate card — not a Grok figure";
  return {
    revenueCad: fee.offeredCad,
    fuelCad: quote.fuelCad,
    landingCad: quote.landingCad,
    handlingCad: quote.handlingCad,
    parkingCad: quote.parkingCad,
    note,
    baseCharterCad: fee.baseCharterCad,
    repositionCad: fee.repositionCad,
    followOnCreditCad: fee.followOnCreditCad,
  };
}

export type MintOnDemandInput = FetchInquiriesInput & {
  destIcao: string;
  /** Optional. Omitted → origin near home / the tail's base. */
  originIcao?: string;
  /** Optional Zulu STD. Omitted → the desk departure picker. */
  departureIso?: string;
  /** Optional. Omitted → a small seasonal load that fits the tail. */
  paxCount?: number;
  cargoLb?: number;
  aircraftId?: string;
  clientName?: string;
  notes?: string;
  techStopIcao?: string;
};

export type MintOnDemandResult =
  | { ok: true; request: CharterRequest }
  | { ok: false; error: string };

/** Inbox on-demand: type a dest ICAO and mint a suitable charter RFQ (tech-stop if needed). */
export function mintOnDemandDestination(input: MintOnDemandInput): MintOnDemandResult {
  const destIcao = (input.destIcao ?? "").trim().toUpperCase();
  if (destIcao.length < 3 || destIcao.length > 4) {
    return { ok: false, error: "Enter a 3–4 letter destination ICAO." };
  }
  const fleet = input.fleet.filter((a) => isCharterAssignable(a));
  const assignable = ratedFleet(fleet, input.ratedIcaoTypes);
  const searchFleet = assignable.length ? assignable : fleet;
  if (!searchFleet.length) return { ok: false, error: "No assignable tail on the fleet." };
  const explicitOrigin = (input.originIcao ?? "").trim().toUpperCase();
  if (explicitOrigin && (explicitOrigin.length < 3 || explicitOrigin.length > 4)) {
    return { ok: false, error: "Enter a 3–4 letter origin ICAO." };
  }
  const namedTail = input.aircraftId ? searchFleet.find((a) => a.id === input.aircraftId) : undefined;
  if (input.aircraftId && !namedTail) return { ok: false, error: "Pick a tail from Fleet." };
  const originGuess = airframeBaseIcao(namedTail ?? searchFleet[0], { homeIcao: input.homeIcao, extraBases: input.extraBases });
  const blocked = contentXmlScheduleBlocked(
    Boolean(input.scheduleOnlyContentXmlAirports),
    inventoryIcaosFromPrefers(input),
    [originGuess, explicitOrigin, destIcao, input.techStopIcao],
  );
  if (blocked) return { ok: false, error: blocked };
  const now = input.now ?? new Date();
  const rng = mulberry32(input.seed ?? seedFromDate(now, destIcao.charCodeAt(0)));

  const dest =
    hydrateSceneryAirport(destIcao, input.scenery) ??
    hydrateSceneryAirport(destIcao, []);
  if (!dest) return { ok: false, error: `Unknown destination ${destIcao}. Add it to Destinations or use a catalog ICAO.` };

  const paxGuess = clampCharterPax(
    input.paxCount != null && Number.isFinite(Number(input.paxCount))
      ? Math.max(0, Math.round(Number(input.paxCount)))
      : 2 + Math.floor(rng() * 5),
    namedTail ?? searchFleet[0]!,
  );
  const cargoLb =
    input.cargoLb != null && Number.isFinite(Number(input.cargoLb))
      ? Math.max(0, Math.round(Number(input.cargoLb)))
      : Math.floor(rng() * 220);
  const specGuess = namedTail ?? searchFleet.find((s) => paxGuess <= s.maxPax) ?? searchFleet[0]!;
  let origin: SceneryAirport | undefined;
  if (explicitOrigin) {
    origin = hydrateSceneryAirport(explicitOrigin, input.scenery) ?? hydrateSceneryAirport(explicitOrigin, []);
    if (!origin) {
      return { ok: false, error: `Unknown origin ${explicitOrigin}. Add it to Destinations or use a catalog ICAO.` };
    }
  } else {
    const originHint = originHintFor(input, now, specGuess).icao;
    const originIcao = (originHint || originGuess || input.homeIcao || "").toUpperCase();
    origin =
      hydrateSceneryAirport(originIcao, input.scenery) ??
      hydrateSceneryAirport(originIcao, []);
    if (!origin || origin.icao.toUpperCase() === dest.icao.toUpperCase()) {
      origin =
        hydrateSceneryAirport(originGuess, input.scenery) ??
        hydrateSceneryAirport(input.homeIcao || "", input.scenery) ??
        hydrateSceneryAirport(input.homeIcao || "", []) ??
        origin;
    }
  }
  if (!origin) return { ok: false, error: "Could not resolve a sensible origin near home / fleet." };
  if (origin.icao.toUpperCase() === dest.icao.toUpperCase()) {
    return { ok: false, error: "Destination matches the origin. Pick another ICAO." };
  }

  const spec =
    namedTail ??
    suitableAircraftForRequest(
      searchFleet,
      { originIcao: origin.icao, destIcao: dest.icao, paxCount: paxGuess, cargoLb, proposedLegs: [] },
      input.scenery,
    )[0] ??
    specGuess;
  const paxCount = clampCharterPax(paxGuess, spec);
  const month = halifaxMonth(now);
  const purpose = pickSeasonalPurpose(month, rng);
  const type = PURPOSE_TO_FLIGHT_TYPE[purpose];
  const flightType = remapUnseasonalType(type, origin.icao, dest.icao, month);
  const party = routeParty(input, origin.icao, dest.icao, rng);
  const named = (input.clientName ?? "").trim();
  const client = named || party.clientName;
  const firm = party.clientCompany;
  const email = party.clientEmail;
  let departure: Date;
  if ((input.departureIso ?? "").trim()) {
    departure = new Date(input.departureIso!);
    if (Number.isNaN(departure.getTime())) {
      return { ok: false, error: "STD must be a valid Zulu date and time." };
    }
  } else {
    departure = departureForDesk(input, now);
  }
  const spokenNotes = (input.notes ?? "").trim();
  const story = storyFor(flightType, client, firm, origin, dest, paxCount, cargoLb, spokenNotes, month, purpose);
  const mail = composeEmail({
    client,
    firm,
    email,
    origin,
    dest,
    departure,
    paxCount,
    cargoLb,
    reason: spokenNotes || `${story.reason} On-demand destination ${dest.icao}.`,
    special: spokenNotes || "On-demand dest ICAO from the desk.",
    type: flightType,
    tone: "broker",
    operatorName: input.operatorName?.trim() || SUGGESTED_COMPANY_NAME,
    timeZone: inboxClockZone(input),
  });
  let row: CharterRequest = {
    id: newId("inq", now),
    createdAt: now.toISOString(),
    source: "automatic",
    status: "new",
    clientName: client,
    clientEmail: email,
    clientCompany: firm,
    originIcao: origin.icao,
    destIcao: dest.icao,
    originName: origin.name,
    destName: dest.name,
    departureIso: departure.toISOString(),
    paxCount,
    cargoLb,
    reason: spokenNotes || `${story.reason} On-demand dest ${dest.icao}. ${seasonCaption(month, halifaxYear(now), deskBasesOf(input))}`,
    specialRequests: spokenNotes || "On-demand dest ICAO from the desk.",
    emailSubject: mail.subject,
    emailBody: mail.body,
    suggestedAircraftId: spec.id,
    flightType,
    seasonalPurpose: purpose,
    tone: "broker",
    warning: missingSceneryWarning(
      dest.icao,
      (input.preferIcaos ?? []).map((icao) => ({
        icao,
        source: "folder_name" as const,
        scannedAtUtc: now.toISOString(),
      })),
      Boolean(input.sceneryGateEnabled && input.preferIcaos?.length),
    ),
  };
  const allowlist = techStopListsFromInput(input);
  const nm = routeDistanceNm(origin, dest, [...input.scenery, dest]);
  if (nm != null && needsTechStop(nm, spec)) {
    const tech = planTechStopRoute({
      origin,
      dest,
      spec,
      paxCount,
      scenery: input.scenery,
      allowlist,
      homeIcao: input.homeIcao,
      manualIcao: input.techStopIcao,
    });
    row = applyTechStopPlanToRequest(row, tech);
    if (input.techStopIcao && tech.pending) {
      const applied = applyManualTechStop({
        request: row,
        stopIcao: input.techStopIcao,
        spec,
        scenery: input.scenery,
        allowlist: allowlist.length ? allowlist : [input.techStopIcao],
        homeIcao: input.homeIcao,
      });
      if ("ok" in applied && applied.ok === false) {
        return { ok: false, error: applied.error };
      }
      if (!("ok" in applied)) row = applied;
    }
  } else {
    const plan = planOutboundItinerary({
      origin,
      dest,
      spec,
      paxCount,
      homeIcao: input.homeIcao,
      scenery: input.scenery,
      rng,
    });
    row = applyItineraryPlan(row, plan);
  }
  const quoted = attachDeskQuotes([row], { ...input, scenery: [...input.scenery, dest] })[0]!;
  return { ok: true, request: quoted };
}

export type CreateManualCharterInput = {
  originIcao: string;
  destIcao: string;
  departureIso: string;
  aircraftId: string;
  paxCount?: number;
  cargoLb?: number;
  notes?: string;
  clientName?: string;
  clientCompany?: string;
  flightType?: FlightType;
  scenery: SceneryAirport[];
  fleet: AircraftSpec[];
  operatorName?: string;
  homeIcao?: string;
  extraBases?: string[];
  rates?: RateTables;
  catalogs?: MoneyCatalogs;
  /** Variable hourly DOC. Quote reserves use a set Mx or engine component. */
  opsDesk?: OpsDeskSettings | null;
  scheduleOnlyContentXmlAirports?: boolean;
  preferIcaos?: string[];
  techStopIcaos?: string[];
  techStopInventory?: string[];
  now?: Date;
  /** Desk zone for the mail "When" line. Omitted uses the home ICAO zone, then UTC. */
  timeZone?: string;
};

export type CreateManualCharterResult =
  | { ok: true; request: CharterRequest }
  | { ok: false; error: string };

/**
 * Dispatcher-written charter RFQ. No Grok, no Classic template.
 * Megan uses the same writer via confirm-gated propose_create_charter.
 * Origin / dest / STD / tail / load are taken from the desk form or spoken args.
 */
export function createManualCharterRequest(input: CreateManualCharterInput): CreateManualCharterResult {
  const originIcao = (input.originIcao ?? "").trim().toUpperCase();
  const destIcao = (input.destIcao ?? "").trim().toUpperCase();
  if (originIcao.length < 3 || originIcao.length > 4) {
    return { ok: false, error: "Enter a 3–4 letter origin ICAO." };
  }
  if (destIcao.length < 3 || destIcao.length > 4) {
    return { ok: false, error: "Enter a 3–4 letter destination ICAO." };
  }
  if (originIcao === destIcao) {
    return { ok: false, error: "Destination matches the origin. Pick another ICAO." };
  }
  const departure = new Date(input.departureIso);
  if (Number.isNaN(departure.getTime())) {
    return { ok: false, error: "STD must be a valid Zulu date and time." };
  }
  const spec = input.fleet.find((a) => a.id === input.aircraftId);
  if (!spec) return { ok: false, error: "Pick a tail from Fleet." };
  if (!isCharterAssignable(spec)) {
    return { ok: false, error: `${spec.registration} is not assignable (grounded / in mx).` };
  }
  const blocked = contentXmlScheduleBlocked(
    Boolean(input.scheduleOnlyContentXmlAirports),
    inventoryIcaosFromPrefers(input),
    [originIcao, destIcao],
  );
  if (blocked) return { ok: false, error: blocked };

  const origin = hydrateSceneryAirport(originIcao, input.scenery);
  const dest = hydrateSceneryAirport(destIcao, input.scenery);
  if (!origin) {
    return { ok: false, error: `Unknown origin ${originIcao}. Add it to Destinations or use a catalog ICAO.` };
  }
  if (!dest) {
    return { ok: false, error: `Unknown destination ${destIcao}. Add it to Destinations or use a catalog ICAO.` };
  }

  const now = input.now ?? new Date();
  const paxCount = clampCharterPax(Math.max(0, Math.round(Number(input.paxCount ?? 2))), spec);
  const cargoLb = Math.max(0, Math.round(Number(input.cargoLb ?? 0)) || 0);
  const notes = (input.notes ?? "").trim();
  const party = regionalParty({
    originIcao,
    destIcao,
    homeIcao: input.homeIcao,
    operatorName: input.operatorName,
  });
  const client = (input.clientName ?? "").trim() || party.clientName;
  const typedFirm = (input.clientCompany ?? "").trim();
  const operator = (input.operatorName ?? "").trim().toLowerCase();
  const firm = typedFirm && typedFirm.toLowerCase() !== operator ? typedFirm : party.clientCompany;
  const flightType = input.flightType && input.flightType !== "ferry" ? input.flightType : "corporate";
  const email = party.clientEmail;
  const special = notes || "Manual desk charter — no Grok.";
  const reason = notes || `Dispatcher-written ${origin.icao}–${dest.icao} on ${spec.registration} (${spec.icaoType}).`;
  const mail = composeEmail({
    client,
    firm,
    email,
    origin,
    dest,
    departure,
    paxCount,
    cargoLb,
    reason,
    special,
    type: flightType,
    tone: "broker",
    operatorName: input.operatorName?.trim() || SUGGESTED_COMPANY_NAME,
    timeZone: inboxClockZone(input),
  });
  let row: CharterRequest = {
    id: newId("inq", now),
    createdAt: now.toISOString(),
    source: "manual",
    status: "new",
    kind: "charter",
    clientName: client,
    clientEmail: email,
    clientCompany: firm,
    originIcao: origin.icao,
    destIcao: dest.icao,
    originName: origin.name,
    destName: dest.name,
    departureIso: departure.toISOString(),
    paxCount,
    cargoLb,
    reason,
    specialRequests: special,
    emailSubject: `Desk charter — ${origin.icao}–${dest.icao}`,
    emailBody: mail.body,
    suggestedAircraftId: spec.id,
    flightType,
    seasonalPurpose: "corporate",
    tone: "broker",
  };
  const allowlist = techStopListsFromInput(input);
  const nm = routeDistanceNm(origin, dest, [...input.scenery, dest, origin]);
  if (nm != null && needsTechStop(nm, spec)) {
    const tech = planTechStopRoute({
      origin,
      dest,
      spec,
      paxCount,
      scenery: input.scenery,
      allowlist,
      homeIcao: input.homeIcao,
    });
    row = applyTechStopPlanToRequest(row, tech);
  } else {
    const plan = planOutboundItinerary({
      origin,
      dest,
      spec,
      paxCount,
      homeIcao: input.homeIcao,
      scenery: input.scenery,
      // Typed OD is the paying leg. Idle / Grok omit allowMultiStop and keep the offer roll.
      allowMultiStop: false,
      rng: () => 1,
    });
    row = applyItineraryPlan(row, plan);
  }
  const quoted = attachDeskQuotes([row], {
    scenery: [...input.scenery, dest, origin],
    fleet: input.fleet,
    rates: input.rates,
    catalogs: input.catalogs,
    opsDesk: input.opsDesk,
    homeIcao: input.homeIcao,
    extraBases: input.extraBases,
  })[0]!;
  return { ok: true, request: quoted };
}

export function attachDeskQuotes(requests: CharterRequest[], input: FetchInquiriesInput): CharterRequest[] {
  return requests.map((row) => {
    const departure = new Date(row.departureIso);
    const hasStd = !Number.isNaN(departure.getTime());
    const subject = stripInventedMoney(row.emailSubject);
    const body = stripInventedMoney(row.emailBody);
    return withRunwayPicWarning(
      {
        ...row,
        emailSubject: hasStd ? alignCharterOnCopy(subject, departure) : subject,
        emailBody: hasStd ? alignCharterOnCopy(body, departure) : body,
        deskQuote: deskQuoteForRequest(row, input) ?? row.deskQuote,
      },
      input.scenery,
    );
  });
}

function sliceBalancedJson(text: string): string | null {
  const startObj = text.indexOf("{");
  const startArr = text.indexOf("[");
  let start = -1;
  if (startObj >= 0 && startArr >= 0) start = Math.min(startObj, startArr);
  else start = Math.max(startObj, startArr);
  if (start < 0) return null;
  const openCh = text[start];
  const closeCh = openCh === "{" ? "}" : "]";
  let depth = 0;
  let inString = false;
  let escape = false;
  for (let i = start; i < text.length; i += 1) {
    const ch = text[i]!;
    if (inString) {
      if (escape) {
        escape = false;
        continue;
      }
      if (ch === "\\") {
        escape = true;
        continue;
      }
      if (ch === "\"") inString = false;
      continue;
    }
    if (ch === "\"") {
      inString = true;
      continue;
    }
    if (ch === openCh) depth += 1;
    else if (ch === closeCh) {
      depth -= 1;
      if (depth === 0) return text.slice(start, i + 1);
    }
  }
  return null;
}

function parseJsonLoose(body: string): unknown {
  try {
    return JSON.parse(body);
  } catch {
    const sliced = sliceBalancedJson(body);
    if (sliced) {
      try {
        return JSON.parse(sliced);
      } catch {
        /* fall through */
      }
    }
    throw new Error("Grok reply was not valid JSON.");
  }
}

export function extractJsonPayload(text: string): unknown {
  const trimmed = String(text ?? "").trim();
  if (!trimmed) throw new Error("Grok returned an empty completion.");
  const fence = trimmed.match(/```(?:json)?\s*([\s\S]*?)```/);
  const body = (fence?.[1] ?? trimmed).trim();
  const parsed = parseJsonLoose(body);
  if (typeof parsed === "string") {
    const inner = parsed.trim();
    if (inner.startsWith("{") || inner.startsWith("[")) {
      try {
        return parseJsonLoose(inner);
      } catch {
        return parsed;
      }
    }
  }
  return parsed;
}

function grokMessageContent(payload: {
  choices?: { message?: { content?: unknown; refusal?: unknown } }[];
}): string {
  const message = payload.choices?.[0]?.message;
  const refusal = typeof message?.refusal === "string" ? message.refusal.trim() : "";
  if (refusal) throw new Error(`Grok refused the inbox request: ${refusal.slice(0, 180)}`);
  const raw = message?.content;
  if (typeof raw === "string") return raw;
  if (Array.isArray(raw)) {
    return raw
      .map((part) => {
        if (typeof part === "string") return part;
        if (part && typeof part === "object") {
          const rec = part as { text?: unknown; content?: unknown };
          if (typeof rec.text === "string") return rec.text;
          if (typeof rec.content === "string") return rec.content;
        }
        return "";
      })
      .join("\n")
      .trim();
  }
  return "";
}

type LooseInquiry = Record<string, unknown>;

function coerceJsonValue(value: unknown): unknown {
  if (typeof value !== "string") return value;
  const inner = value.trim();
  if (!inner || (inner[0] !== "{" && inner[0] !== "[")) return value;
  try {
    return extractJsonPayload(inner);
  } catch {
    return value;
  }
}

function unwrapInquiryList(raw: unknown): LooseInquiry[] {
  let value = coerceJsonValue(raw);
  if (Array.isArray(value)) {
    return value.filter((r) => r && typeof r === "object") as LooseInquiry[];
  }
  if (!value || typeof value !== "object") return [];
  const obj = value as Record<string, unknown>;
  const nested = coerceJsonValue(obj.inquiries ?? obj.requests ?? obj.items ?? obj.data ?? obj.result);
  if (Array.isArray(nested)) {
    return nested.filter((r) => r && typeof r === "object") as LooseInquiry[];
  }
  if (nested && typeof nested === "object") {
    const inner = nested as Record<string, unknown>;
    if (inner.originIcao || inner.origin || inner.destIcao || inner.destination) {
      return [inner as LooseInquiry];
    }
    const deeper = inner.inquiries ?? inner.requests ?? inner.items;
    if (Array.isArray(deeper)) {
      return deeper.filter((r) => r && typeof r === "object") as LooseInquiry[];
    }
  }
  if (obj.originIcao || obj.origin || obj.destIcao || obj.destination) {
    return [obj as LooseInquiry];
  }
  return [];
}

function asString(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value.trim() : fallback;
}

function optionalArriveByIso(value: unknown): string | undefined {
  const text = asString(value);
  if (!text) return undefined;
  const parsed = Date.parse(text);
  if (!Number.isFinite(parsed)) return undefined;
  return new Date(parsed).toISOString();
}

function asNumber(value: unknown, fallback: number): number {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function asType(value: unknown): FlightType {
  const raw = asString(value).toLowerCase();
  if ((FLIGHT_TYPES as string[]).includes(raw) || raw === "ferry") return raw as FlightType;
  if (raw.includes("golf")) return "golf";
  if (raw.includes("sun") || raw.includes("florida")) return "sun";
  if (raw.includes("vip") || raw.includes("leisure")) return "vip";
  if (raw.includes("med")) return "medical";
  return "corporate";
}

function parseDeparture(raw: unknown, input: FetchInquiriesInput, now: Date, rng?: () => number): Date {
  const stdRng = rng ?? stdRngFor(input, now, 3);
  const pick = {
    now,
    aircraftState: input.aircraftState,
    dutyStandby: input.dutyStandby,
    rng: stdRng,
  };
  const missing = new Date(0);
  const parsed = parseInboxDeparture(raw, now, missing);
  if (parsed.getTime() !== 0) return applyInboxStdBias(parsed, pick);
  return pickInboxDeparture(pick);
}

export function normalizeGrokInquiries(raw: unknown, input: FetchInquiriesInput): CharterRequest[] {
  const now = input.now ?? new Date();
  const stdRng = stdRngFor(input, now, 3);
  const fleet = input.fleet.filter((a) => isCharterAssignable(a));
  const assignable = ratedFleet(fleet, input.ratedIcaoTypes);
  const routeFleet = input.hardFilterRatings ? assignable : assignable.length ? assignable : fleet;
  if (input.hardFilterRatings && !routeFleet.length) return [];
  const rows = unwrapInquiryList(raw);

  const out: CharterRequest[] = [];
  for (const row of rows) {
    const rawPax = Math.max(0, Math.min(16, Math.round(asNumber(row.paxCount ?? row.passengers, 2))));
    const cargoLb = Math.max(0, Math.round(asNumber(row.cargoLb ?? row.cargo, 0)));
    const maxSeat = Math.max(1, ...routeFleet.map((a) => a.maxPax), ...fleet.map((a) => a.maxPax));
    const paxCount = Math.min(rawPax, maxSeat);
    const route = coerceRoute(
      asString(row.originIcao ?? row.origin, ""),
      asString(row.destIcao ?? row.destination, ""),
      input.scenery,
      routeFleet,
      paxCount,
      cargoLb,
      input.homeIcao,
      input.extraBases,
    );
    if (!route) continue;

    const fits = suitableAircraft(fleet, route.origin, route.dest, paxCount, cargoLb, input.scenery);
    const suggestedId = asString(row.suggestedAircraft ?? row.suggestedAircraftId ?? row.aircraftId);
    const spec =
      pickAssignedAircraft(fits, input.ratedIcaoTypes, suggestedId) ??
      pickAssignedAircraft(
        fleet.filter((a) => isCharterAssignable(a) && paxCount <= a.maxPax && cargoLb <= cargoCapLb(a, paxCount)),
        input.ratedIcaoTypes,
        suggestedId,
      );
    if (!spec) continue;
    const seated = clampCharterPax(paxCount, spec);

    const month = halifaxMonth(now);
    const party = settleParty(
      {
        clientName: asString(row.clientName ?? row.name),
        clientCompany: asString(row.clientCompany ?? row.company),
        clientEmail: asString(row.clientEmail ?? row.email),
      },
      {
        originIcao: route.origin.icao,
        destIcao: route.dest.icao,
        homeIcao: input.homeIcao,
        operatorName: input.operatorName,
        rng: stdRng,
      },
    );
    const client = party.clientName;
    const firm = party.clientCompany;
    const email = party.clientEmail;
    const rawMx = Boolean(row.mxPositioning) || asString(row.mxPositioning).toLowerCase() === "true";
    const payingMx =
      rawMx && seated + cargoLb > 0 && destIsMroForType(route.dest.icao, spec.icaoType, input.mxBases);
    const remapped = remapUnseasonalType(asType(row.flightType ?? row.type), route.origin.icao, route.dest.icao, month);
    const type = payingMx && remapped === "ferry" ? "corporate" : remapped;
    const grokPurpose = asString(row.seasonalPurpose).toLowerCase();
    const purpose: SeasonalPurposeId = payingMx
      ? "mx_payload"
      : grokPurpose === "connection"
        ? "connection"
        : purposeFromFlightType(type, month, route.dest.icao);
    const special = asString(row.specialRequests ?? row.notes);
    const departure = parseDeparture(pickStdRaw(row), input, now, stdRng);
    const toneRaw = asString(row.tone).toLowerCase();
    const tone: InquiryTone = toneRaw === "casual" ? "casual" : "broker";
    const story = storyFor(type, client, firm, route.origin, route.dest, seated, cargoLb, special, month, purpose);
    // Grok's copy names the route it asked for; once the desk remaps a field it no longer describes the ticket.
    const rerouted = route.rerouted;
    const reason = rerouted ? story.reason : asString(row.reason ?? row.purpose, `${firm} charter`);
    const mail = composeEmail({
      client,
      firm,
      email,
      origin: route.origin,
      dest: route.dest,
      departure,
      paxCount: seated,
      cargoLb,
      reason: reason || story.reason,
      special,
      type,
      tone,
      operatorName: input.operatorName?.trim() || SUGGESTED_COMPANY_NAME,
      timeZone: inboxClockZone(input),
    });

    out.push({
      id: newId("inq", now),
      createdAt: now.toISOString(),
      source: "grok",
      status: "new",
      clientName: client,
      clientEmail: email,
      clientCompany: firm,
      originIcao: route.origin.icao,
      destIcao: route.dest.icao,
      originName: route.origin.name,
      destName: route.dest.name,
      departureIso: departure.toISOString(),
      paxCount: seated,
      cargoLb,
      reason: reason || story.reason,
      specialRequests: special,
      emailSubject: rerouted ? mail.subject : alignCharterOnCopy(asString(row.emailSubject, mail.subject), departure),
      emailBody: rerouted ? mail.body : alignCharterOnCopy(asString(row.emailBody, mail.body), departure),
      suggestedAircraftId: spec.id,
      requestedAircraft: asString(row.requestedAircraft ?? row.requestedAircraftType).slice(0, 32) || undefined,
      arriveByIso: optionalArriveByIso(row.arriveByIso),
      flightType: type,
      seasonalPurpose: purpose,
      mxPositioning: payingMx,
      itineraryKind: hydrateItineraryKind(row.itineraryKind),
      techStopIcao: asString(row.viaIcao || row.techStopIcao) || undefined,
      returnEmpty: hydrateItineraryKind(row.itineraryKind) === "round_trip_empty",
      tone,
      warning: route.warning,
      destPack: undefined,
    });
  }
  return attachDeskQuotes(
    decorateGeneratedInquiries(out, input, mulberry32(seedFromDate(now, out.length + 11)), false, true),
    input,
  );
}

function fleetPromptLines(fleet: AircraftSpec[], homeIcao?: string, extraBases?: string[]): string {
  return fleet
    .filter((a) => isCharterAssignable(a))
    .map((a) => {
      const base = airframeBaseIcao(a, { homeIcao, extraBases });
      return `- id=${a.id} ${a.name} ${a.registration} type=${a.icaoType} base=${base} maxPax=${a.maxPax} rangeNm=${a.rangeNm} minRunwayFt=${a.minRunwayFt}`;
    })
    .join("\n");
}

function sceneryPromptLines(scenery: SceneryAirport[], cap = 0): string {
  const rows = cap > 0 ? scenery.slice(0, cap) : scenery;
  const extra = cap > 0 && scenery.length > rows.length ? `\n- … ${scenery.length - rows.length} more allow-list ICAOs omitted for idle mint` : "";
  return `${rows.map((s) => `- ${s.icao} ${s.name} rwy=${s.runwayFt}ft ${s.city}`).join("\n")}${extra}`;
}

/** Idle's 48-cap must still show legal dests after remint / cool-down. */
function sceneryForPrompt(input: FetchInquiriesInput, now: Date): SceneryAirport[] {
  const avoidance = mintContextOf(input, now);
  const bases = deskBasesOf(input);
  const ordered = (rows: SceneryAirport[]) => orderByBaseGravity(rows, bases, input.scenery);
  if (!avoidance.hardDests.size) return ordered(input.scenery);
  const unused = input.scenery.filter((s) => !avoidance.hardDests.has(s.icao.toUpperCase()));
  if (unused.length < 2) return ordered(input.scenery);
  const used = input.scenery.filter((s) => avoidance.hardDests.has(s.icao.toUpperCase()));
  return [...ordered(unused), ...ordered(used)];
}

function grokInboxNudgeBlock(input: FetchInquiriesInput, count: number): string {
  const nudge = input.inboxGrokNudge ?? "none";
  const pairs = (input.inboxAvoidPairs ?? []).filter(Boolean).slice(0, 16);
  if (nudge === "json_only") {
    return `RETRY: Your previous reply was not usable JSON. Reply with JSON only — no markdown fences, no trailing prose. Shape: {"inquiries":[{...}]}. inquiries MUST contain at least ${count} allow-list city-pair${count === 1 ? "" : "s"}.`;
  }
  if (nudge === "nonempty") {
    return `RETRY: inquiries was empty. Destinations and an assignable airframe exist — emit at least ${count} legal allow-list inquiry object${count === 1 ? "" : "s"}. An empty array is a failed answer.`;
  }
  if (nudge === "relaxed") {
    return `RELAXED: Variety, remint, and cool-down hints are now SOFT. Prefer unused dests, but you MUST still emit at least ${count} legal allow-list inquir${count === 1 ? "y" : "ies"}. An empty inquiries array is not allowed.`;
  }
  if (nudge === "avoid_pairs" || pairs.length) {
    return `DO NOT USE these city-pairs: ${pairs.join(", ") || "(see mint memory)"}. Pick other allow-list origin/dest pairs. inquiries MUST be non-empty.`;
  }
  return "";
}

export function buildGrokInboxPrompt(input: FetchInquiriesInput, count: number): string {
  const operator = input.operatorName?.trim() || SUGGESTED_COMPANY_NAME;
  const rated = ratedFleet(input.fleet, input.ratedIcaoTypes);
  const now = input.now ?? new Date();
  const month = halifaxMonth(now);
  const bases = deskBasesOf(input);
  const promptScenery = sceneryForPrompt(input, now);
  const destSample = nearBaseDestSample(promptScenery, bases, 12, input.scenery);
  const originHint = originHintFor(input, now);
  const mxOpp = findMxPayloadOpportunity({
    fleet: input.fleet,
    scenery: input.scenery,
    squawks: input.squawks,
    melItems: input.melItems,
    homeIcao: input.homeIcao,
    mxBases: input.mxBases,
  });
  const board = (input.existingTrips ?? [])
    .slice(0, 8)
    .map((t) => `${t.originIcao}–${t.destIcao} ${t.departureIso}${t.registration ? ` ${t.registration}` : ""}`)
    .join("; ");
  return [
    `Generate ${count} realistic incoming charter inquiry emails for ${operator} (primary base ${input.homeIcao || "(set Home Base)"}${bases.length > 1 ? `; all current bases ${bases.join(", ")}` : ""}).`,
    `Northstar is the software vendor — do not address emails to Northstar. The operator is ${operator}.`,
    `Season / region: ${seasonCaption(month, halifaxYear(now), bases)}. ${seasonHintForPrompt(month, halifaxYear(now), bases)}`,
    `Realism rules:`,
    grokRealismRulesFor(bases),
    formatCharterOpsBriefPromptBlock({
      title: input.charterOpsBriefTitle,
      body: input.charterOpsBrief,
    }),
    formatMintAvoidancePrompt(mintContextOf(input, now), promptScenery, bases),
    formatCrewPrefMintPrompt(input.crewPrefs ?? []),
    grokDestinationGravityLine(bases, destSample),
    `Customer RFQ origins follow the full current base list (${bases.join(", ") || "(set Home Base)"}) and the suggested tail's assigned base (fleet lines: base=ICAO). Prefer ${originHint.icao || "(none)"} (${originHint.reason}). If the tail is parked at one of those bases, leave from that field. Do not default originIcao to CYPD or any leftover fiction pad, last landing, parked field, or nearest-to-position airport unless that ICAO is a current base — position is for assignment and ferries, not inbox RFQs.`,
    aircraftStatePromptLine(input.aircraftState),
    formatStandbyStdPromptBlock(input.dutyStandby, now),
    (input.mxBases ?? []).length
      ? `Company MX bases (the only shops): ${(input.mxBases ?? []).map((row) => `${row.icao}${row.description ? ` (${row.description})` : ""}`).join("; ")}. Do not invent a maintenance ICAO.`
      : "This company has no MX bases. Do not invent a shop and do not set mxPositioning.",
    mxOpp
      ? `Mx positioning: ${mxOpp.spec.registration} (${mxOpp.spec.icaoType}) at ${mxOpp.origin.icao} needs ${mxOpp.mro.description || mxOpp.mro.icao} (${mxOpp.dest.icao}). Prefer one paying (pax or cargo) cheaper-than-usual charter on ${mxOpp.origin.icao}–${mxOpp.dest.icao} (set mxPositioning=true, suggestedAircraft=${mxOpp.spec.id}) instead of an empty ferry. An empty ferry on that leg is not revenue.`
      : "",
    board ? `Existing trips on the board (avoid obvious double-books; occasional overlap is OK): ${board}.` : "",
    input.dutyRemainMin != null
      ? `Crew remaining duty/flight window about ${Math.max(0, Math.round(input.dutyRemainMin))} minutes (company CAR clock — default 704 Commuter). Prefer STDs that fit.`
      : "",
    grokInboxNudgeBlock(input, count),
    `Return JSON only: {"inquiries":[{...}]}. No markdown fences, no trailing prose.`,
    `MUST return a non-empty inquiries array with at least ${count} object${count === 1 ? "" : "s"} when the Destinations allow-list has two or more fields and an assignable airframe exists. An empty inquiries array is a failed answer.`,
    `Each inquiry MUST use originIcao and destIcao from the destinations allow-list (never invent ICAOs).`,
    `Passengers, cargo, and route MUST fit a type-rated company tail. Prefer/require suggestedAircraft from this rated set:`,
    fleetPromptLines(rated.length ? rated : input.fleet, input.homeIcao, input.extraBases),
    `Do not auto-assign unrated types (e.g. PC-12) unless no rated tail is legal.`,
    `Do not invent company cash, paid quotes, dollar amounts, or a ledger balance — the desk attaches a rate-card quote after you return.`,
    `When the customer names an aircraft type or class, set requestedAircraft to that ICAO designator or class label and repeat it in specialRequests. Leave requestedAircraft blank when they did not ask. Set arriveByIso only when they name an arrival deadline. Do not invent a type.`,
    `Tone mix: include both formal broker-style (tone=broker) and casual first-person client (tone=casual). Vary subject lines.`,
    input.dutyStandby?.armed
      ? `Fields: clientName, clientEmail, clientCompany, originIcao, destIcao, departureIso (ISO-8601 scheduled departure / STD; majority inside the armed Duty Standby window / soon enough to accept and go, smaller share 1–2 days ahead), paxCount, cargoLb, reason, specialRequests, flightType (golf|sun|vip|corporate|medical|ferry — ferry only for an empty company positioning with no paying pax/cargo), seasonalPurpose (golf|sun|corporate|vip|medical|family|hunting|fishing|ski|tourism|connection — use connection only for a missed-airline / connecting-flight hub story), tone (broker|casual), emailSubject, emailBody, suggestedAircraft (rated fleet id), mxPositioning (true only for a paying mx-positioning charter someone would take; never on an empty ferry), itineraryKind (one_way|round_trip_empty|backhaul|multi_stop|tech_stop), viaIcao (optional intermediate for multi_stop or tech_stop).`
      : `Fields: clientName, clientEmail, clientCompany, originIcao, destIcao, departureIso (ISO-8601 scheduled departure / STD, next 14 days), paxCount, cargoLb, reason, specialRequests, flightType (golf|sun|vip|corporate|medical|ferry — ferry only for an empty company positioning with no paying pax/cargo), seasonalPurpose (golf|sun|corporate|vip|medical|family|hunting|fishing|ski|tourism|connection — use connection only for a missed-airline / connecting-flight hub story), tone (broker|casual), emailSubject, emailBody, suggestedAircraft (rated fleet id), mxPositioning (true only for a paying mx-positioning charter someone would take; never on an empty ferry), itineraryKind (one_way|round_trip_empty|backhaul|multi_stop|tech_stop), viaIcao (optional intermediate for multi_stop or tech_stop).`,
    `Most outbounds that end away from home imply an empty return ferry (baked into the desk quote, not a second pax sector) unless you also include a cheaper reverse backhaul RFQ (itineraryKind=backhaul, paying charter).`,
    `If the city-pair is beyond the suggested tail’s practical range, set itineraryKind=tech_stop and viaIcao only from the tech-stop addon list — never invent a stock airport the desk does not have. Tech-stop hops are not pax revenue.`,
    techStopListsFromInput(input).length
      ? `Tech-stop addon ICAOs (only these, plus a desk manual pick): ${techStopListsFromInput(input).join(", ")}.`
      : `Tech-stop addon list is empty — leave viaIcao blank (TBD / pick me) rather than inventing a stock field.`,
    `departureIso is the trip STD — not the email Date / received / created time. Any "charter on …" date in emailSubject or emailBody MUST be that STD's calendar day in America/Halifax (Atlantic).`,
    ``,
    `Full fleet on the line (PC-12 may be listed but is not the company pilot rating unless noted):`,
    fleetPromptLines(input.fleet, input.homeIcao, input.extraBases),
    ``,
    `Destinations allow-list:`,
    sceneryPromptLines(promptScenery, input.inboxPromptKind === "idle" ? 48 : 0),
    input.scheduleOnlyContentXmlAirports
      ? `MUST: schedule only ICAOs from the scanned Content.xml / Community list (same tokens Destinations / map already use): ${inventoryIcaosFromPrefers(input).join(", ") || "(empty — mint nothing)"}. Do not use Destinations-only or OurAirports fields that are not on that list. OurAirports runway lengths still apply.`
      : input.sceneryGateEnabled && input.preferIcaos?.length
        ? `Soft-prefer destinations the assigned crew has in their Community scan (folder-name heuristic): ${input.preferIcaos.join(", ")}. You may still use other allow-list ICAOs; do not invent ICAOs.`
        : "",
  ].filter(Boolean).join("\n");
}

export type CallGrokInboxOptions = {
  nudge?: GrokInboxNudge;
  avoidPairs?: string[];
  responseFormat?: GrokInboxResponseFormat;
};

export function formatGrokHttpError(status: number, body = ""): string {
  const snippet = body.trim().slice(0, 180);
  if (status === 401 || status === 403) {
    return `xAI rejected the API key (HTTP ${status})${snippet ? `: ${snippet}` : "."}`;
  }
  if (status === 429) {
    return `xAI rate-limited Inbox Fetch (HTTP 429). Wait a minute${snippet ? ` — ${snippet}` : "."}`;
  }
  if (status >= 500) {
    return `xAI Grok is unavailable (HTTP ${status})${snippet ? `: ${snippet}` : "."}`;
  }
  return `xAI Grok HTTP ${status}${snippet ? `: ${snippet}` : ""}`;
}

export function grokInboxErrorStatus(err: unknown): number | null {
  if (err && typeof err === "object" && "status" in err && typeof (err as { status: unknown }).status === "number") {
    return (err as { status: number }).status;
  }
  const msg = err instanceof Error ? err.message : String(err ?? "");
  const m = msg.match(/HTTP (\d{3})/);
  return m ? Number(m[1]) : null;
}

export function isRetryableGrokInboxError(err: unknown): boolean {
  const status = grokInboxErrorStatus(err);
  if (status == null) return true;
  return status === 429 || status >= 500;
}

export function isGrokInboxKeyRejected(err: unknown): boolean {
  const status = grokInboxErrorStatus(err);
  return status === 401 || status === 403;
}

export function isGrokInboxSchemaUnsupported(err: unknown): boolean {
  const status = grokInboxErrorStatus(err);
  if (status !== 400) return false;
  const msg = err instanceof Error ? err.message : String(err ?? "");
  return /json_schema|response_format|schema/i.test(msg);
}

function grokHttpError(status: number, body = ""): Error {
  const err = new Error(formatGrokHttpError(status, body));
  (err as Error & { status: number }).status = status;
  return err;
}

function grokInboxTemperature(nudge: GrokInboxNudge | undefined): number {
  if (nudge === "json_only") return 0.2;
  if (nudge === "relaxed") return 0.95;
  return 0.9;
}

async function postGrokInbox(
  apiKey: string,
  input: FetchInquiriesInput,
  count: number,
  fetchImpl: typeof fetch,
  format: GrokInboxResponseFormat,
): Promise<unknown> {
  const res = await fetchImpl(XAI_CHAT_URL, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${apiKey}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      model:
        input.inboxModel?.trim() ||
        (input.inboxPromptKind === "idle" ? GROK_IDLE_INBOX_MODEL : XAI_INBOX_MODEL),
      temperature: grokInboxTemperature(input.inboxGrokNudge),
      response_format: grokInboxResponseFormat(format),
      messages: [
        {
          role: "system",
          content: GROK_INBOX_SYSTEM,
        },
        { role: "user", content: buildGrokInboxPrompt(input, count) },
      ],
    }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw grokHttpError(res.status, text);
  }
  const payload = (await res.json()) as {
    choices?: { message?: { content?: unknown; refusal?: unknown } }[];
    error?: { message?: string };
  };
  if (payload.error?.message) throw new Error(payload.error.message);
  const content = grokMessageContent(payload);
  if (!content) throw new Error("Grok returned an empty completion.");
  return extractJsonPayload(content);
}

export async function callGrokInbox(
  apiKey: string,
  input: FetchInquiriesInput,
  count: number,
  fetchImpl: typeof fetch = fetch,
  opts?: CallGrokInboxOptions,
): Promise<unknown> {
  const ready: FetchInquiriesInput = {
    ...input,
    inboxGrokNudge: opts?.nudge ?? input.inboxGrokNudge ?? "none",
    inboxAvoidPairs: opts?.avoidPairs ?? input.inboxAvoidPairs,
  };
  const preferred: GrokInboxResponseFormat =
    opts?.responseFormat ?? (ready.inboxPromptKind === "idle" ? "json_object" : "json_schema");
  try {
    return await postGrokInbox(apiKey, ready, count, fetchImpl, preferred);
  } catch (err) {
    if (preferred === "json_schema" && isGrokInboxSchemaUnsupported(err)) {
      return await postGrokInbox(apiKey, ready, count, fetchImpl, "json_object");
    }
    throw err;
  }
}

function classicInboxRequests(ready: FetchInquiriesInput): CharterRequest[] {
  return dropBlockedInquiries(
    ready.forceDemo ? generateDemoInquiries(ready) : generateSeasonalInquiries(ready),
    ready,
  );
}

function classicInboxMode(input: FetchInquiriesInput): InquirySource {
  return input.jobMode === "automatic" ? "automatic" : "demo";
}

function withClassicFallback(
  ready: FetchInquiriesInput,
  reason: string,
  lastResort = false,
): FetchInquiriesResult {
  const requests = classicInboxRequests(ready);
  const suffix = lastResort ? "Classic templates used as last resort." : "Classic templates used.";
  if (requests.length) {
    return {
      ok: true,
      mode: classicInboxMode(ready),
      requests,
      fallbackReason: `${reason} ${suffix}`,
    };
  }
  return { ok: false, error: reason };
}

type GrokSoftKind = "parse" | "empty" | "filtered";

function nextGrokInboxNudge(input: {
  lastSoft: GrokSoftKind | null;
}): GrokInboxNudge {
  if (input.lastSoft === "parse") return "json_only";
  if (input.lastSoft === "empty") return "nonempty";
  return "none";
}

function classicReasonAfterGrokBudget(lastSoft: GrokSoftKind | null, lastHard: string | null): string {
  if (lastHard) return lastHard;
  if (lastSoft === "filtered") return GROK_INBOX_FILTERS_EMPTIED;
  if (lastSoft === "empty") return GROK_INBOX_EMPTY_AFTER_RETRY;
  if (lastSoft === "parse") return GROK_INBOX_PARSE_AFTER_RETRY;
  return GROK_INBOX_UNUSABLE;
}

export async function fetchCharterInquiries(input: FetchInquiriesInput): Promise<FetchInquiriesResult> {
  if (mockGrokEnabled()) {
    input = { ...input, apiKey: MOCK_GROK_KEY, fetchImpl: mockGrokFetch() };
  }
  const count = input.count ?? 2;
  if ((input.scenery?.length ?? 0) < 2) {
    return { ok: false, error: "Need at least two destinations to raise inquiries." };
  }
  const assignable = (input.fleet ?? []).filter((a) => isCharterAssignable(a));
  const mxReady = findMxPayloadOpportunity({
    fleet: input.fleet ?? [],
    scenery: input.scenery,
    squawks: input.squawks,
    melItems: input.melItems,
    homeIcao: input.homeIcao,
    mxBases: input.mxBases,
  });
  if (!assignable.length && !mxReady) {
    return { ok: false, error: "No available airframes. Add one on the Fleet tab." };
  }

  const scheduled = withScheduledScenery({ ...input, fleet: input.fleet ?? [], count });
  if ("error" in scheduled) {
    return { ok: false, error: scheduled.error };
  }
  const ready: FetchInquiriesInput = scheduled;

  if (input.forceDemo || !input.apiKey?.trim()) {
    const requests = classicInboxRequests(ready);
    if (!requests.length) {
      return { ok: false, error: "No legal city-pair for the current fleet / destinations." };
    }
    return { ok: true, mode: classicInboxMode(input), requests };
  }

  const key = input.apiKey.trim();
  const fetchImpl = input.fetchImpl ?? fetch;
  const kindBudget = grokInboxCallBudget(input.inboxPromptKind);
  const budget =
    input.inboxCallBudget != null && Number.isFinite(input.inboxCallBudget)
      ? Math.max(0, Math.min(kindBudget, Math.floor(input.inboxCallBudget)))
      : kindBudget;
  if (budget <= 0) {
    return { ok: false, error: "Idle Inbox daily call budget is spent." };
  }
  let lastSoft: GrokSoftKind | null = null;
  let lastHard: string | null = null;
  const avoidPairs = hardAvoidPairLabels(mintContextOf(ready));
  let lastContentLegalGrok: CharterRequest[] = [];

  for (let attempt = 0; attempt < budget; attempt += 1) {
    const nudge = nextGrokInboxNudge({ lastSoft });
    try {
      const raw = await callGrokInbox(key, ready, count, fetchImpl, {
        nudge,
        avoidPairs,
      });
      const normalized = normalizeGrokInquiries(raw, ready);
      const requests = dropBlockedInquiries(normalized, ready);
      if (requests.length) {
        return { ok: true, mode: "grok", requests };
      }
      const contentLegal = contentXmlLegalInquiries(normalized, ready);
      if (contentLegal.length) lastContentLegalGrok = contentLegal;
      if (normalized.length) {
        lastSoft = "filtered";
        // Avoidance was already in attempt 0. Do not remint on a filter wipe.
        break;
      }
      lastSoft = "empty";
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Grok request failed.";
      if (isGrokInboxKeyRejected(err)) {
        return withClassicFallback(ready, msg);
      }
      const parseFail = /not valid JSON|empty completion|unreadable/i.test(msg);
      if (parseFail) {
        lastSoft = "parse";
        lastHard = null;
        continue;
      }
      lastHard = msg;
      recordDiagnostic({
        module: "grok-inbox",
        severity: "error",
        message: msg,
        stack: err instanceof Error ? err.stack : undefined,
      });
      if (isRetryableGrokInboxError(err) && attempt + 1 < budget) {
        continue;
      }
      return withClassicFallback(ready, msg);
    }
  }

  // Large Destinations / scan: Grok minted usable (Content.xml-legal) rows.
  // Remint / cool-down emptied the hard-filtered set — keep Grok, never Classic.
  if (lastContentLegalGrok.length && allowlistIsLarge(ready)) {
    return { ok: true, mode: "grok", requests: lastContentLegalGrok };
  }

  return withClassicFallback(ready, classicReasonAfterGrokBudget(lastSoft, lastHard), true);
}

export function draftFromRequest(
  request: CharterRequest,
  input: {
    scenery: SceneryAirport[];
    fleet: AircraftSpec[];
    aircraftId?: string;
    now?: Date;
    username?: string;
    ratedIcaoTypes?: string[];
    company?: RadioCompany | null;
    scheduleOnlyContentXmlAirports?: boolean;
    contentXmlIcaos?: string[];
    mxBases?: readonly CompanyMxBase[];
  },
): DraftFromRequestResult {
  const blocked = contentXmlScheduleBlocked(
    Boolean(input.scheduleOnlyContentXmlAirports),
    input.contentXmlIcaos,
    icaosFromRoute(request),
  );
  if (blocked) return { ok: false, error: blocked };
  const origin = hydrateSceneryAirport(request.originIcao, input.scenery);
  const dest = hydrateSceneryAirport(request.destIcao, input.scenery);
  if (!origin || !dest) {
    return { ok: false, error: "Origin or destination is not on the destinations list." };
  }

  const fleet = input.fleet.filter((a) => isCharterAssignable(a));
  const mxTail =
    request.mxPositioning
      ? input.fleet.find((a) => a.id === (input.aircraftId || request.suggestedAircraftId) && canCarryMxPayload(a))
      : undefined;
  const hopFits = suitableAircraftForRequest(fleet, request, input.scenery);
  const fits = [
    ...(hopFits.length
      ? hopFits
      : suitableAircraft(fleet, origin, dest, request.paxCount, request.cargoLb, input.scenery)),
    ...(mxTail ? [mxTail] : []),
  ];
  const explicit = input.aircraftId
    ? [...fleet, ...(mxTail ? [mxTail] : [])].find((a) => a.id === input.aircraftId)
    : undefined;
  const spec =
    explicit ??
    pickAssignedAircraft(fits, input.ratedIcaoTypes, request.suggestedAircraftId);

  if (!spec) {
    return { ok: false, error: "No airframe on file for this request." };
  }

  const departure = new Date(request.departureIso);
  const paxCount = Math.min(request.paxCount, spec.maxPax);
  const paxNames = people(paxCount);
  const now = input.now ?? new Date();
  const month = halifaxMonth(now);
  const year = halifaxYear(now);
  const payingMx = isPayingMxPositioning(request);
  const emptyFerry = isEmptyFerry(request);
  const flightType = payingMx
    ? request.flightType === "ferry"
      ? "corporate"
      : request.flightType
    : emptyFerry
      ? "ferry"
      : request.flightType === "ferry"
        ? "corporate"
        : request.flightType;
  const purposeId: SeasonalPurposeId =
    payingMx
      ? "mx_payload"
      : emptyFerry
        ? "ferry"
        : ((request.seasonalPurpose as SeasonalPurposeId | undefined) ??
          purposeFromFlightType(flightType, month, dest.icao));
  const story = storyFor(
    flightType,
    request.clientName,
    request.clientCompany,
    origin,
    dest,
    paxCount,
    request.cargoLb,
    request.specialRequests,
    month,
    purposeId,
  );

  if (requestNeedsTechStopPick(request)) {
    return { ok: false, error: "Pick a tech-stop ICAO before filing this RFQ." };
  }
  const paying = payingOrTechStopLegs(request.proposedLegs).filter(
    (leg) => leg.originIcao !== "TBD" && leg.destIcao !== "TBD",
  );
  const legs = [];
  if (paying.length > 1 && !emptyFerry) {
    let cursor = departure;
    for (let i = 0; i < paying.length; i += 1) {
      const hop = paying[i]!;
      const hopOrigin = hydrateSceneryAirport(hop.originIcao, input.scenery);
      const hopDest = hydrateSceneryAirport(hop.destIcao, input.scenery);
      if (!hopOrigin || !hopDest) {
        return { ok: false, error: "Could not resolve a multi-stop field." };
      }
      const hopPax = Math.min(hop.paxCount || paxCount, spec.maxPax);
      const hopNames = i === 0 ? paxNames : people(hopPax);
      const computed = computeLeg(i + 1, hopOrigin, hopDest, spec, cursor, hopPax, hopNames, hop.notes || story.purpose);
      if (!computed) {
        return { ok: false, error: "Could not compute a multi-stop leg — missing coordinates." };
      }
      legs.push(computed);
      cursor = new Date(cursor.getTime() + (computed.eteMin + 40) * 60_000);
    }
  } else {
    const single = computeLeg(1, origin, dest, spec, departure, emptyFerry ? 0 : paxCount, emptyFerry ? [] : paxNames, story.purpose);
    if (!single) {
      return { ok: false, error: "Could not compute the leg — missing coordinates." };
    }
    legs.push(single);
  }
  const leg = legs[0]!;
  const last = legs[legs.length - 1]!;

  const mission: DraftMission = withLegs({
    id: newId("ns", now),
    createdAt: now.toISOString(),
    callsign: missionCallsign(spec, input.company),
    aircraftId: spec.id,
    aircraftName: spec.name,
    registration: spec.registration,
    flightType,
    originIcao: origin.icao,
    destIcao: last.destIcao,
    originName: origin.name,
    destName: last.destName,
    originCity: origin.city,
    destCity: last.destCity,
    distanceNm: legs.reduce((s, l) => s + l.distanceNm, 0),
    eteMin: legs.reduce((s, l) => s + l.eteMin, 0),
    fuelGal: legs.reduce((s, l) => s + l.fuelGal, 0),
    fuelLbs: legs.reduce((s, l) => s + l.fuelLbs, 0),
    trackTrue: leg.trackTrue,
    revenueFactor: request.quoteDiscount,
    paxCount: emptyFerry ? 0 : paxCount,
    paxNames: emptyFerry ? [] : paxNames,
    cargoLb: emptyFerry ? 0 : request.cargoLb,
    scenario: `${alignCharterOnCopy(request.emailSubject, departure)}\n\n${story.scenario}`,
    purpose: story.purpose,
    departureIso: departure.toISOString(),
    weatherNote: seasonCaption(month, year),
    originMetar: leg.originMetar,
    destMetar: leg.destMetar,
    isMaintenance: emptyFerry && destIsMroForType(dest.icao, spec.icaoType, input.mxBases),
    mxPositioning: payingMx,
    originRunwayFt: origin.runwayFt,
    destRunwayFt: last.destRunwayFt,
    legs,
    source: "inbox",
    seasonNote: seasonCaption(month, year),
    inboxRequestId: request.id,
  });

  return {
    ok: true,
    plan: flightPlanFromDraft(mission, {
      fleet: input.fleet,
      username: input.username,
      cargoLb: request.cargoLb,
      requestId: request.id,
    }),
  };
}
